import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { useSession } from '../context/SessionContext';
import { trackEvent, trackPageView } from '../services/analytics';
import { rlog } from '../utils/railwayLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to parse time strings like "12:17 PM" or "14:30"
const parseTimeString = (timeStr) => {
  if (!timeStr) return { hour: 12, minute: 0 };
  const str = String(timeStr).trim();
  
  // Handle "HH:MM AM/PM" format
  const ampmMatch = str.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/);
  if (ampmMatch) {
    let hour = parseInt(ampmMatch[1]);
    const minute = parseInt(ampmMatch[2]);
    const period = ampmMatch[3]?.toUpperCase();
    
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    
    return { hour, minute };
  }
  
  // Handle numeric hour
  const numHour = parseInt(str);
  if (!isNaN(numHour)) return { hour: numHour, minute: 0 };
  
  return { hour: 12, minute: 0 };
};

// Helper to parse date strings like "14-05-1997" or "1997-05-14"
const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  const str = String(dateStr).trim();
  
  // DD-MM-YYYY format
  const ddmmyyyy = str.match(/(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (ddmmyyyy) {
    return { day: parseInt(ddmmyyyy[1]), month: parseInt(ddmmyyyy[2]), year: parseInt(ddmmyyyy[3]) };
  }
  
  // YYYY-MM-DD format
  const yyyymmdd = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (yyyymmdd) {
    return { year: parseInt(yyyymmdd[1]), month: parseInt(yyyymmdd[2]), day: parseInt(yyyymmdd[3]) };
  }
  
  return null;
};

const getInitialBirthDetails = () => {
  // PRIORITY 1: Read from birthDetails (most reliable - from onboarding)
  const keys = ['birthDetails', 'birth_details', 'completeBirthData'];
  for (const key of keys) {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const data = JSON.parse(stored);
        let year = data.year || data.birthYear;
        let month = data.month || data.birthMonth;
        let day = data.day || data.birthDay;
        
        const dateStr = data.dateOfBirth || data.birth_date;
        if (dateStr) {
          const parsed = parseDateString(dateStr);
          if (parsed) {
            year = year || parsed.year;
            month = month || parsed.month;
            day = day || parsed.day;
          }
        }
        
        let hour = data.hour;
        let minute = data.minute;
        const timeStr = data.timeOfBirth || data.time_of_birth;
        if (timeStr) {
          const parsed = parseTimeString(timeStr);
          hour = hour ?? parsed.hour;
          minute = minute ?? parsed.minute;
        }
        
        if (year) {
          console.log(`[DomainInsights] Birth details from ${key}: ${year}-${month}-${day} ${hour}:${minute} lat=${data.latitude} lon=${data.longitude}`);
          return {
            name: data.name || 'User',
            year: parseInt(year),
            month: parseInt(month) || 1,
            day: parseInt(day) || 1,
            hour: parseInt(hour) || 12,
            minute: parseInt(minute) || 0,
            place: data.place || data.placeOfBirth || 'India',
            gender: data.gender || 'unknown',
            latitude: parseFloat(data.latitude) || 28.6139,
            longitude: parseFloat(data.longitude) || 77.209
          };
        }
      }
    } catch (e) { /* ignore */ }
  }
  
  // PRIORITY 2: Fallback to unified_story_data
  try {
    const stored = localStorage.getItem('unified_story_data');
    if (stored) {
      const data = JSON.parse(stored);
      const u = data?.user_info;
      if (u) {
        let year = u.year || u.birth_year;
        let month = u.month || u.birth_month;
        let day = u.day || u.birth_day;
        
        const dateStr = u.dateOfBirth || u.birth_date;
        if (dateStr && typeof dateStr === 'string') {
          const parsed = parseDateString(dateStr);
          if (parsed) {
            year = year || parsed.year;
            month = month || parsed.month;
            day = day || parsed.day;
          }
        }
        
        let hour = u.hour || u.birth_hour;
        let minute = u.minute || u.birth_minute;
        const timeStr = u.timeOfBirth || u.time_of_birth;
        if (timeStr && typeof timeStr === 'string') {
          const parsed = parseTimeString(timeStr);
          hour = hour ?? parsed.hour;
          minute = minute ?? parsed.minute;
        }
        
        if (year) {
          return {
            name: u.name || 'User',
            year: parseInt(year),
            month: parseInt(month) || 1,
            day: parseInt(day) || 1,
            hour: parseInt(hour) || 12,
            minute: parseInt(minute) || 0,
            place: u.place || u.placeOfBirth || u.birth_place || 'India',
            gender: u.gender || 'unknown',
            latitude: parseFloat(u.latitude) || 28.6139,
            longitude: parseFloat(u.longitude) || 77.209
          };
        }
      }
    }
  } catch (e) { console.error('Error parsing unified_story_data:', e); }
  
  return null;
};

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Planet configurations for each domain - realistic planet visuals
const DOMAIN_PLANETS = {
  1: { // Health - Sun
    name: 'Sun',
    mainColor: '#FFD700',
    planets: [
      { type: 'sun', size: 80, x: 10, y: 15 },
      { type: 'star', size: 4, x: 75, y: 25 },
      { type: 'star', size: 3, x: 85, y: 45 },
    ]
  },
  2: { // Career - Saturn (with rings)
    name: 'Saturn',
    mainColor: '#D4A574',
    planets: [
      { type: 'saturn', size: 70, x: 75, y: 20 },
      { type: 'star', size: 3, x: 15, y: 35 },
      { type: 'star', size: 4, x: 30, y: 55 },
    ]
  },
  3: { // Wealth - Jupiter (large with bands)
    name: 'Jupiter',
    mainColor: '#E8B87D',
    planets: [
      { type: 'jupiter', size: 90, x: 5, y: 10 },
      { type: 'star', size: 3, x: 80, y: 30 },
      { type: 'star', size: 4, x: 70, y: 60 },
    ]
  },
  4: { // Education - Mercury (small, grey)
    name: 'Mercury',
    mainColor: '#A0A0A0',
    planets: [
      { type: 'mercury', size: 45, x: 70, y: 25 },
      { type: 'star', size: 4, x: 20, y: 40 },
      { type: 'star', size: 3, x: 35, y: 65 },
    ]
  },
  5: { // Marriage - Venus (bright, yellowish)
    name: 'Venus',
    mainColor: '#F5DEB3',
    planets: [
      { type: 'venus', size: 55, x: 15, y: 20 },
      { type: 'star', size: 3, x: 75, y: 35 },
      { type: 'star', size: 4, x: 85, y: 55 },
    ]
  },
  6: { // Family - Moon (craters)
    name: 'Moon',
    mainColor: '#E8E8E8',
    planets: [
      { type: 'moon', size: 65, x: 70, y: 15 },
      { type: 'star', size: 4, x: 15, y: 45 },
      { type: 'star', size: 3, x: 30, y: 70 },
    ]
  },
  7: { // Travel - Mars (red)
    name: 'Mars',
    mainColor: '#CD5C5C',
    planets: [
      { type: 'mars', size: 55, x: 20, y: 25 },
      { type: 'star', size: 3, x: 80, y: 40 },
      { type: 'star', size: 4, x: 65, y: 65 },
    ]
  },
  8: { // Spirituality - Neptune (blue)
    name: 'Neptune',
    mainColor: '#4169E1',
    planets: [
      { type: 'neptune', size: 60, x: 65, y: 20 },
      { type: 'star', size: 4, x: 15, y: 35 },
      { type: 'star', size: 3, x: 25, y: 60 },
    ]
  },
};

// Planet Component - renders realistic CSS planets
const Planet = ({ type, size, style }) => {
  const planetStyles = {
    sun: {
      background: 'radial-gradient(circle at 30% 30%, #FFF7E0 0%, #FFD700 30%, #FFA500 60%, #FF8C00 100%)',
      boxShadow: '0 0 60px #FFD700, 0 0 100px #FFA50080, inset -10px -10px 30px rgba(0,0,0,0.2)',
    },
    saturn: {
      background: 'radial-gradient(circle at 35% 35%, #F5DEB3 0%, #D4A574 40%, #8B7355 80%, #654321 100%)',
      boxShadow: '0 0 40px #D4A57450, inset -8px -8px 20px rgba(0,0,0,0.3)',
    },
    jupiter: {
      background: `
        radial-gradient(circle at 35% 35%, #FFE4B5 0%, #E8B87D 30%, #CD853F 60%, #8B4513 100%)
      `,
      boxShadow: '0 0 50px #E8B87D40, inset -10px -10px 25px rgba(0,0,0,0.3)',
    },
    mercury: {
      background: 'radial-gradient(circle at 35% 35%, #D3D3D3 0%, #A0A0A0 40%, #696969 80%, #404040 100%)',
      boxShadow: '0 0 25px #A0A0A030, inset -6px -6px 15px rgba(0,0,0,0.4)',
    },
    venus: {
      background: 'radial-gradient(circle at 35% 35%, #FFFACD 0%, #F5DEB3 40%, #DEB887 80%, #D2691E 100%)',
      boxShadow: '0 0 35px #F5DEB350, inset -7px -7px 18px rgba(0,0,0,0.2)',
    },
    moon: {
      background: 'radial-gradient(circle at 35% 35%, #FFFFFF 0%, #E8E8E8 30%, #C0C0C0 60%, #808080 100%)',
      boxShadow: '0 0 30px #E8E8E830, inset -8px -8px 20px rgba(0,0,0,0.3)',
    },
    mars: {
      background: 'radial-gradient(circle at 35% 35%, #FFA07A 0%, #CD5C5C 40%, #8B0000 80%, #4A0000 100%)',
      boxShadow: '0 0 35px #CD5C5C40, inset -7px -7px 18px rgba(0,0,0,0.3)',
    },
    neptune: {
      background: 'radial-gradient(circle at 35% 35%, #87CEEB 0%, #4169E1 40%, #0000CD 80%, #00008B 100%)',
      boxShadow: '0 0 40px #4169E140, inset -8px -8px 20px rgba(0,0,0,0.3)',
    },
  };

  if (type === 'star') {
    return (
      <div style={{
        ...style,
        width: size,
        height: size,
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 0 6px #fff, 0 0 10px #fff',
      }} />
    );
  }

  return (
    <div style={{
      ...style,
      width: size,
      height: size,
      borderRadius: '50%',
      position: 'relative',
      ...planetStyles[type],
    }}>
      {/* Saturn's rings */}
      {type === 'saturn' && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) rotateX(75deg)',
          width: size * 1.8,
          height: size * 0.5,
          border: '3px solid rgba(210, 180, 140, 0.6)',
          borderRadius: '50%',
          boxShadow: '0 0 10px rgba(210, 180, 140, 0.3)',
        }} />
      )}
      {/* Jupiter's bands */}
      {type === 'jupiter' && (
        <>
          <div style={{ position: 'absolute', top: '25%', left: '10%', right: '10%', height: '8%', background: 'rgba(139,69,19,0.3)', borderRadius: '50%', filter: 'blur(2px)' }} />
          <div style={{ position: 'absolute', top: '45%', left: '5%', right: '5%', height: '12%', background: 'rgba(160,82,45,0.25)', borderRadius: '50%', filter: 'blur(3px)' }} />
          <div style={{ position: 'absolute', top: '65%', left: '10%', right: '10%', height: '8%', background: 'rgba(139,69,19,0.3)', borderRadius: '50%', filter: 'blur(2px)' }} />
          {/* Great Red Spot */}
          <div style={{ position: 'absolute', top: '40%', left: '55%', width: '20%', height: '15%', background: 'rgba(178,34,34,0.5)', borderRadius: '50%', filter: 'blur(2px)' }} />
        </>
      )}
      {/* Moon craters */}
      {type === 'moon' && (
        <>
          <div style={{ position: 'absolute', top: '20%', left: '30%', width: '15%', height: '15%', background: 'rgba(128,128,128,0.4)', borderRadius: '50%', boxShadow: 'inset 2px 2px 4px rgba(0,0,0,0.3)' }} />
          <div style={{ position: 'absolute', top: '50%', left: '55%', width: '20%', height: '20%', background: 'rgba(128,128,128,0.35)', borderRadius: '50%', boxShadow: 'inset 2px 2px 5px rgba(0,0,0,0.3)' }} />
          <div style={{ position: 'absolute', top: '65%', left: '25%', width: '12%', height: '12%', background: 'rgba(128,128,128,0.4)', borderRadius: '50%', boxShadow: 'inset 1px 1px 3px rgba(0,0,0,0.3)' }} />
        </>
      )}
      {/* Mars polar cap */}
      {type === 'mars' && (
        <div style={{ position: 'absolute', top: '5%', left: '30%', width: '40%', height: '15%', background: 'rgba(255,255,255,0.4)', borderRadius: '50%', filter: 'blur(3px)' }} />
      )}
      {/* Neptune atmosphere bands */}
      {type === 'neptune' && (
        <>
          <div style={{ position: 'absolute', top: '30%', left: '10%', right: '10%', height: '6%', background: 'rgba(135,206,250,0.3)', borderRadius: '50%', filter: 'blur(2px)' }} />
          <div style={{ position: 'absolute', top: '55%', left: '5%', right: '5%', height: '8%', background: 'rgba(0,0,139,0.2)', borderRadius: '50%', filter: 'blur(3px)' }} />
        </>
      )}
    </div>
  );
};

// Scrolling Planets Background
const ScrollingPlanets = ({ activeDomain }) => {
  const currentPlanetConfig = DOMAIN_PLANETS[activeDomain] || DOMAIN_PLANETS[1];
  const mainColor = currentPlanetConfig.mainColor;
  
  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
      {/* Ambient glow that changes with domain */}
      <motion.div
        key={`glow-${activeDomain}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `radial-gradient(ellipse at 30% 20%, ${mainColor}20 0%, transparent 50%), 
                       radial-gradient(ellipse at 70% 60%, ${mainColor}10 0%, transparent 40%)`,
        }}
      />

      {/* Main planets that change with section */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`planets-${activeDomain}`}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{ position: 'absolute', inset: 0 }}
        >
          {currentPlanetConfig.planets.map((planet, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                left: `${planet.x}%`,
                top: `${planet.y}%`,
              }}
              animate={{
                y: [0, -20, 0],
                x: [0, i % 2 === 0 ? 15 : -15, 0],
              }}
              transition={{
                duration: 6 + i * 2,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: i * 0.5,
              }}
            >
              <Planet 
                type={planet.type} 
                size={planet.size} 
                style={{ opacity: planet.type === 'star' ? 0.7 : 0.85 }}
              />
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>

      {/* Static small stars */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={`star-${i}`}
          style={{
            position: 'absolute',
            left: `${(i * 17 + 5) % 100}%`,
            top: `${(i * 23 + 7) % 100}%`,
            width: i % 3 === 0 ? 3 : 2,
            height: i % 3 === 0 ? 3 : 2,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 0 4px #fff',
          }}
          animate={{ opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: 2 + (i % 3), delay: i * 0.15, repeat: Infinity }}
        />
      ))}

      {/* Floating dust particles with domain color */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={`dust-${i}`}
          style={{
            position: 'absolute',
            left: `${10 + (i * 7) % 80}%`,
            top: '100%',
            width: 2,
            height: 2,
            borderRadius: '50%',
            background: mainColor,
            boxShadow: `0 0 6px ${mainColor}`,
          }}
          animate={{
            y: [0, -800],
            opacity: [0, 0.6, 0],
          }}
          transition={{
            duration: 10 + i * 2,
            delay: i * 1.5,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
};

// Section Accordion
const Section = ({ title, children, aiContent, domainKey, sectionKey, parentOpen, aiContentLoading, isPremium, aiFetchDone, aiFetchFailed, onRetryAi }) => {
  const [open, setOpen] = useState(false);
  
  // Sync with parent domain open/close
  useEffect(() => {
    if (parentOpen === true) setOpen(true);
    else if (parentOpen === false) setOpen(false);
  }, [parentOpen]);
  
  // Get AI content for this specific section if available
  let aiSectionContent = null;
  if (aiContent?.life_insights && domainKey && sectionKey) {
    const domainData = aiContent.life_insights[domainKey];
    if (domainData && typeof domainData === 'object') {
      aiSectionContent = domainData[sectionKey];
    }
  }
  
  // If no children and no AI content, skip
  if (!children && !aiSectionContent) return null;
  
  // Determine what to show: AI content > shimmer (while loading) > retry (premium failed) > children
  const showAi = aiSectionContent && aiSectionContent.length > 10;
  // Premium users: NEVER show old algorithm content. Show shimmer until AI fetch completes.
  const showShimmer = !showAi && (aiContentLoading || (isPremium && !aiFetchDone));
  // Premium users who had AI fail: show retry button, NOT algorithm content
  const showRetry = !showAi && !showShimmer && isPremium && aiFetchFailed;
  // Only show algorithm content for non-premium users
  const showChildren = !showAi && !showShimmer && !showRetry;
  
  return (
    <div style={{ marginBottom: '1px' }}>
      <button onClick={() => setOpen(!open)} style={{ width: '100%', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: open ? 'rgba(116,179,255,0.04)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(116,179,255,0.06)', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(12px, 3.3vw, 15px)',
            color: '#F3E5AB',
            fontWeight: 400,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>{title}</span>
          {showAi && (
            <span style={{ fontSize: 'clamp(8px, 2vw, 9px)', color: 'rgba(147,51,234,0.8)', fontWeight: '500', padding: '2px 6px', background: 'rgba(147,51,234,0.15)', borderRadius: '4px' }}>AI</span>
          )}
        </div>
        <motion.span animate={{ rotate: open ? 45 : 0 }} style={{ fontSize: 'clamp(13px, 3.5vw, 16px)', color: 'rgba(116,179,255,0.5)', fontWeight: '300' }}>+</motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} style={{ overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px 24px', background: 'rgba(10,15,25,0.4)' }}>
              {showAi ? (
                <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', lineHeight: '1.9', color: 'rgba(255,255,255,0.75)', fontWeight: '400', padding: '10px 12px', background: 'rgba(147,51,234,0.05)', borderRadius: '8px', borderLeft: '2px solid rgba(147,51,234,0.3)' }}>
                  {aiSectionContent}
                </p>
              ) : showShimmer ? (
                <div style={{ padding: '10px 12px' }}>
                  {[1,2,3].map(i => (
                    <div key={i} style={{ height: '12px', marginBottom: '10px', borderRadius: '6px', background: 'linear-gradient(90deg, rgba(147,51,234,0.08) 25%, rgba(147,51,234,0.15) 50%, rgba(147,51,234,0.08) 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', width: i === 3 ? '60%' : '100%' }} />
                  ))}
                  <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
                </div>
              ) : showRetry ? (
                <div style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.45)', marginBottom: '10px' }}>AI insights loading failed</p>
                  <button data-testid="retry-ai-insights" onClick={onRetryAi} style={{ padding: '8px 20px', borderRadius: '6px', fontSize: 'clamp(10px, 2.8vw, 12px)', background: 'rgba(147,51,234,0.15)', color: 'rgba(147,51,234,0.9)', border: '1px solid rgba(147,51,234,0.3)', cursor: 'pointer' }}>Retry</button>
                </div>
              ) : (
                children
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Text
const Text = ({ children }) => {
  if (!children || children === 'null') return null;
  return <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', lineHeight: '1.9', color: 'rgba(255,255,255,0.65)', fontWeight: '400' }}>{children}</p>;
};

// Strength Bars
const StrengthBars = ({ items }) => {
  if (!items?.length) return null;
  const levels = { 'Very Strong': 4, 'Strong': 3, 'Moderate': 2, 'Sensitive': 1 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {items.map((item, i) => (
        <div key={i} style={{ padding: '14px 16px', background: 'rgba(116,179,255,0.02)', borderRadius: '10px', border: '1px solid rgba(116,179,255,0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.8)', fontWeight: '400' }}>{item.level}</span>
            <div style={{ display: 'flex', gap: '3px' }}>
              {[1,2,3,4].map(n => <div key={n} style={{ width: '14px', height: '3px', borderRadius: '1px', background: n <= (levels[item.level] || 2) ? '#74B3FF' : 'rgba(116,179,255,0.1)' }} />)}
            </div>
          </div>
          <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.6', color: 'rgba(255,255,255,0.5)', fontWeight: '400' }}>{item.systems}</p>
        </div>
      ))}
    </div>
  );
};

// Tags
const Tags = ({ items }) => {
  if (!items?.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {items.filter(i => i).map((item, i) => (
        <span key={i} style={{ padding: '8px 14px', borderRadius: '20px', fontSize: 'clamp(10px, 2.8vw, 12px)', background: 'rgba(116,179,255,0.06)', color: 'rgba(255,255,255,0.7)', fontWeight: '400' }}>{item}</span>
      ))}
    </div>
  );
};

// Phases
const Phases = ({ data }) => {
  if (!data) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {Object.entries(data).filter(([k, v]) => v).map(([key, val], i) => (
        <div key={i} style={{ padding: '14px 16px', background: 'rgba(116,179,255,0.02)', borderRadius: '10px', borderLeft: '2px solid rgba(116,179,255,0.2)' }}>
          <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '6px', textTransform: 'capitalize', fontWeight: '400' }}>{key.replace(/_/g, ' ')}</p>
          <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.7', color: 'rgba(255,255,255,0.6)', fontWeight: '400' }}>{val}</p>
        </div>
      ))}
    </div>
  );
};

// Profile Cards
const ProfileCards = ({ data }) => {
  if (!data) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[{ l: 'Physical', v: data.physical }, { l: 'Personality', v: data.personality }, { l: 'Profession', v: data.profession }, { l: 'Family', v: data.family_background }].filter(i => i.v).map((item, i) => (
        <div key={i} style={{ padding: '14px 16px', background: 'rgba(116,179,255,0.02)', borderRadius: '10px' }}>
          <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '6px', fontWeight: '400' }}>{item.l}</p>
          <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.7', color: 'rgba(255,255,255,0.6)', fontWeight: '400' }}>{item.v}</p>
        </div>
      ))}
    </div>
  );
};

// Mode Cards
const ModeCards = ({ data }) => {
  if (!data) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {data.job && <div style={{ padding: '14px 16px', background: 'rgba(116,179,255,0.02)', borderRadius: '10px' }}><p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '6px', fontWeight: '400' }}>Employment</p><p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.7', color: 'rgba(255,255,255,0.6)', fontWeight: '400' }}>{data.job}</p></div>}
      {data.consulting && <div style={{ padding: '14px 16px', background: 'rgba(116,179,255,0.02)', borderRadius: '10px' }}><p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '6px', fontWeight: '400' }}>Consulting</p><p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.7', color: 'rgba(255,255,255,0.6)', fontWeight: '400' }}>{data.consulting}</p></div>}
      {data.business && <div style={{ padding: '14px 16px', background: 'rgba(116,179,255,0.02)', borderRadius: '10px' }}><p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '6px', fontWeight: '400' }}>Business</p><p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.7', color: 'rgba(255,255,255,0.6)', fontWeight: '400' }}>{data.business}</p></div>}
      {data.final_mode && <div style={{ padding: '14px 16px', background: 'rgba(116,179,255,0.04)', borderRadius: '10px' }}><p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.7', color: 'rgba(255,255,255,0.7)', fontWeight: '400' }}>{data.final_mode}</p></div>}
    </div>
  );
};

// Sectors
const Sectors = ({ favorable, caution }) => {
  if (!favorable?.length && !caution?.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {favorable?.length > 0 && <div><p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.5)', marginBottom: '8px', fontWeight: '400' }}>FAVORABLE</p><Tags items={favorable} /></div>}
      {caution?.length > 0 && <div><p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.35)', marginBottom: '8px', fontWeight: '400' }}>CAUTION</p><div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>{caution.map((t, i) => <span key={i} style={{ padding: '8px 14px', borderRadius: '20px', fontSize: 'clamp(10px, 2.8vw, 12px)', background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.45)', fontWeight: '400' }}>{t}</span>)}</div></div>}
    </div>
  );
};

// Insight
const Insight = ({ children, domainNum }) => {
  if (!children) return null;
  const color = DOMAIN_PLANETS[domainNum]?.mainColor || '#74B3FF';
  
  // Sanitize technical terms for end users
  let sanitizedText = typeof children === 'string' ? children : children;
  if (typeof sanitizedText === 'string') {
    sanitizedText = sanitizedText
      .replace(/PURNA AYUH/gi, 'long life indicated')
      .replace(/Purna Ayuh/gi, 'long life indicated')
      .replace(/MADHYA AYUH/gi, 'moderate life span')
      .replace(/ALPA AYUH/gi, 'attention to health needed')
      .replace(/attention dysregulation/gi, 'focus management')
      .replace(/persistent attention dysregulation/gi, 'tendency toward distraction')
      .replace(/recoverable fire constitution/gi, 'strong resilient constitution')
      .replace(/\(MODERATE\)/gi, '')
      .replace(/\(HIGH\)/gi, '')
      .replace(/\(LOW\)/gi, '')
      .replace(/\|/g, '•')
      .trim();
  }
  
  return (
    <div style={{ margin: '16px 20px 20px', padding: '16px', background: `${color}10`, borderRadius: '12px', borderLeft: `2px solid ${color}50` }}>
      <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', lineHeight: '1.8', color: 'rgba(255,255,255,0.65)', fontWeight: '400' }}>{sanitizedText}</p>
    </div>
  );
};

// AI content is now integrated directly into Section components
// No separate AiSummaryCard needed

// Domain Section Component - Clean version with AI content support
const DomainSection = ({ num, title, children, onInView, introOpen, aiContent, domainKey, aiContentLoading, isPremium, aiFetchDone, aiFetchFailed, onRetryAi }) => {
  const [open, setOpen] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState(null); // null=initial, true/false=toggle
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: '-40% 0px -40% 0px' });
  const planetConfig = DOMAIN_PLANETS[num];
  const mainColor = planetConfig.mainColor;
  const mainPlanet = planetConfig.planets[0];
  
  useEffect(() => {
    if (isInView && onInView) onInView(num);
  }, [isInView, num, onInView]);

  // Intro animation - open briefly when introOpen is true
  useEffect(() => {
    if (introOpen) {
      setOpen(true);
      setSectionsOpen(true);
    } else if (introOpen === false) {
      setOpen(false);
      setSectionsOpen(false);
    }
  }, [introOpen]);

  // Toggle handler for domain header
  const handleToggle = () => {
    const newOpen = !open;
    setOpen(newOpen);
    setSectionsOpen(newOpen);
  };

  // Map section titles to backend keys
  const SECTION_KEY_MAP = {
    // Health
    'constitution': 'constitution',
    'effect': 'effect', 
    'behavioral triggers': 'behavioral_triggers',
    'areas requiring attention': 'areas_requiring_attention',
    'mental & emotional structure': 'mental___emotional_structure',
    'life expectancy': 'life_expectancy',
    'current phase': 'current_phase',
    'developmental phase': 'developmental_phase',
    'seasonal health': 'seasonal_health',
    'past health phases': 'past_health_phases',
    'constitution profile': 'constitution_profile',
    'life health timeline': 'life_health_timeline',
    'comprehensive health profile': 'comprehensive_health_profile',
    // Career
    'work orientation': 'work_orientation',
    'career model': 'career_model',
    'what works': 'what_works',
    'what fails': 'what_fails',
    'entrepreneurial potential': 'entrepreneurial_potential',
    'career path analysis': 'career_path_analysis',
    'career timeline': 'career_timeline',
    // Wealth
    'financial potential': 'financial_potential',
    'your wealth path': 'your_wealth_path',
    'how you build wealth': 'how_you_build_wealth',
    'wealth potential': 'wealth_potential',
    'financial habits': 'financial_habits',
    'current financial phase': 'current_financial_phase',
    'income sources': 'income_sources',
    'financial cautions': 'financial_cautions',
    'financial timeline': 'financial_timeline',
    // Education
    'knowledge as authority': 'knowledge_as_authority',
    'learning style': 'learning_style',
    'best subjects': 'best_subjects',
    'credentials impact': 'credentials_impact',
    'education timeline': 'education_timeline',
    'higher education': 'higher_education',
    // Marriage
    'marriage timing': 'marriage_timing',
    'marriage overview': 'marriage_overview',
    'partner profile': 'partner_profile',
    'relationship compatibility': 'relationship_compatibility',
    'relationship timing': 'relationship_timing',
    'relationship strengths': 'relationship_strengths',
    'emotional nature': 'emotional_nature',
    'spouse profile': 'spouse_profile',
    'relationship risks': 'relationship_risks',
    'marriage summary': 'marriage_summary',
    'when relationships form': 'relationship_timing',
    'marital harmony': 'relationship_compatibility',
    // Family
    'family role': 'family_role',
    'parents': 'parents',
    'children': 'children',
    'home life': 'home_life',
    'family timeline': 'family_timeline',
    'ancestral property': 'ancestral_property',
    // Travel
    'travel nature': 'travel_nature',
    'foreign connections': 'foreign_connections',
    'travel timing': 'travel_timing',
    'favorable directions': 'favorable_directions',
    'travel timeline': 'travel_timeline',
    'foreign settlement': 'foreign_settlement',
    // Spiritual
    'spiritual path': 'spiritual_path',
    'spiritual evolution': 'spiritual_evolution',
    'recommended practices': 'recommended_practices',
    'liberation': 'liberation',
    'guru connection': 'guru_connection',
    'past life karma': 'past_life_karma',
    // Extra aliases
    'best career fields': 'career_model',
    'career challenges': 'what_fails',
    'wealth sources': 'income_sources',
    'business potential': 'business_potential',
    'current alerts': 'current_phase',
    'health vitality index': 'health_vitality_index',
    // More aliases for unmapped sections
    'foreign travel': 'foreign_connections',
    'settlement abroad': 'foreign_settlement',
    'religious nature': 'spiritual_path',
    'liberation potential': 'liberation',
    'relationship dynamics': 'relationship_compatibility',
    'when relationships form': 'relationship_timing',
    'marital harmony': 'relationship_compatibility',
    'comprehensive health profile': 'comprehensive_health_profile',
    'developmental phase': 'developmental_phase',
    'seasonal health': 'seasonal_health',
    'past health phases': 'past_health_phases',
    'constitution profile': 'constitution_profile',
    'life health timeline': 'life_health_timeline',
    'current phase': 'current_phase',
    'financial timeline': 'financial_timeline',
    'family timeline': 'family_timeline',
    'education timeline': 'education_timeline',
    'travel timeline': 'travel_timing',
    'guru connection': 'guru_connection',
    'past life karma': 'past_life_karma',
    'home life': 'home_life',
    'ancestral property': 'ancestral_property'
  };

  // Recursively clone children and inject aiContent and domainKey into ALL Section components
  const injectAiProps = (children) => {
    return React.Children.map(children, child => {
      if (!React.isValidElement(child)) return child;
      
      if (child.type === Section) {
        const title = child.props.title?.toLowerCase() || '';
        const sectionKey = SECTION_KEY_MAP[title] || title.replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
        return React.cloneElement(child, { 
          aiContent, 
          domainKey,
          sectionKey,
          parentOpen: sectionsOpen,
          aiContentLoading,
          isPremium,
          aiFetchDone,
          aiFetchFailed,
          onRetryAi
        });
      }
      
      // Recursively process children of non-Section elements (divs, fragments, etc.)
      if (child.props?.children) {
        return React.cloneElement(child, {}, injectAiProps(child.props.children));
      }
      
      return child;
    });
  };
  
  const enhancedChildren = injectAiProps(children);

  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ delay: num * 0.05 }} 
      style={{ 
        marginBottom: '12px', 
        borderRadius: '16px', 
        overflow: 'hidden', 
        background: isInView ? `linear-gradient(135deg, ${mainColor}15 0%, rgba(18,24,36,0.7) 100%)` : 'rgba(18,24,36,0.6)', 
        border: isInView ? `1px solid ${mainColor}30` : '1px solid rgba(116,179,255,0.06)',
        boxShadow: isInView ? `0 0 40px ${mainColor}15` : 'none',
        transition: 'all 0.5s ease',
      }}
    >
      <button onClick={handleToggle} style={{ width: '100%', padding: '20px', display: 'flex', alignItems: 'center', gap: '16px', background: 'none', border: 'none', cursor: 'pointer' }}>
        <motion.div 
          animate={{ scale: isInView ? [1, 1.1, 1] : 1 }}
          transition={{ duration: 2, repeat: isInView ? Infinity : 0 }}
          style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            overflow: 'hidden',
            boxShadow: isInView ? `0 0 20px ${mainColor}40` : 'none',
            transition: 'all 0.5s ease',
          }}
        >
          <Planet type={mainPlanet.type} size={40} />
        </motion.div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(14px, 3.8vw, 18px)',
              color: '#F3E5AB',
              fontWeight: 400,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}>{title}</span>
            {aiContent?.life_insights?.[domainKey] && (
              <span style={{ fontSize: 'clamp(8px, 2vw, 9px)', color: 'rgba(147,51,234,0.9)', fontWeight: '500', padding: '2px 6px', background: 'rgba(147,51,234,0.15)', borderRadius: '4px' }}>AI</span>
            )}
          </div>
          <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: isInView ? mainColor : 'rgba(255,255,255,0.4)', fontWeight: '400', transition: 'color 0.5s' }}>{planetConfig.name}</span>
        </div>
        <motion.span animate={{ rotate: open ? 45 : 0 }} style={{ fontSize: 'clamp(14px, 4vw, 18px)', color: isInView ? mainColor : 'rgba(116,179,255,0.4)', fontWeight: '300', transition: 'color 0.5s' }}>+</motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} transition={{ duration: 0.25 }} style={{ overflow: 'hidden' }}>
            <div style={{ borderTop: `1px solid ${mainColor}20` }}>
              {enhancedChildren}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ===== MONTHLY MYSTERY REPORTS SECTION =====
const MysteryReportsSection = () => {
  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  React.useEffect(() => {
    const calc = () => {
      const now = new Date();
      const eom = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      const d = eom - now;
      if (d <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return { days: Math.floor(d / 86400000), hours: Math.floor((d / 3600000) % 24), minutes: Math.floor((d / 60000) % 60), seconds: Math.floor((d / 1000) % 60) };
    };
    setTimeLeft(calc());
    const t = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(t);
  }, []);
  
  const monthName = React.useMemo(() => new Date().toLocaleString('default', { month: 'long' }), []);
  
  const reports = [
    { id: 'career_shift', title: 'Career Shift Alert', sub: 'Next big move timing', accent: '#c8a860' },
    { id: 'hidden_talent', title: 'Hidden Talent', sub: 'Nakshatra abilities', accent: '#74b3ff' },
    { id: 'wealth_window', title: 'Wealth Window', sub: 'Financial windows', accent: '#ffc864' },
    { id: 'relationship_energy', title: 'Relationship Map', sub: 'Compatibility shifts', accent: '#c88ca0' },
    { id: 'health_shield', title: 'Health Shield', sub: 'Protection periods', accent: '#2ecc71' },
  ];
  
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div data-testid="mystery-reports-section" style={{ padding: '14px 0', borderBottom: '1px solid rgba(116,179,255,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', marginBottom: '10px' }}>
        <p style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(13px, 3.5vw, 16px)',
          color: '#F3E5AB',
          fontWeight: 400,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          margin: 0,
        }}>{monthName} Reports</p>
        <div data-testid="mystery-countdown-timer" style={{ display: 'flex', alignItems: 'center', gap: '3px', padding: '3px 7px', borderRadius: '6px', background: 'rgba(116,179,255,0.04)', border: '1px solid rgba(116,179,255,0.08)' }}>
          <span style={{ fontSize: '9px', color: 'rgba(116,179,255,0.6)', fontWeight: '600', fontFamily: 'monospace' }}>{pad(timeLeft.days)}d {pad(timeLeft.hours)}h {pad(timeLeft.minutes)}m</span>
        </div>
      </div>
      
      {/* Horizontal scroll cards */}
      <div className="mystery-hscroll" style={{ 
        display: 'flex', gap: '8px', overflowX: 'auto', padding: '0 16px 4px 16px',
        scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch',
      }}>
        <style>{`.mystery-hscroll::-webkit-scrollbar { display: none; }`}</style>
        {reports.map((r) => (
          <div key={r.id} data-testid={`mystery-card-${r.id}`} style={{ 
            minWidth: '130px', maxWidth: '150px', padding: '10px 12px', borderRadius: '10px', 
            background: `${r.accent}08`, border: `1px solid ${r.accent}15`, flexShrink: 0,
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: '6px', right: '6px', padding: '1px 5px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontSize: '7px', color: 'rgba(255,255,255,0.25)', fontWeight: '600', letterSpacing: '0.05em' }}>LOCKED</span>
            </div>
            <p style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(11px, 3vw, 12px)',
              color: '#F3E5AB',
              fontWeight: 500,
              letterSpacing: '0.06em',
              margin: '0 0 2px 0',
              paddingRight: '36px',
              lineHeight: '1.3',
            }}>{r.title}</p>
            <p style={{ fontSize: '8px', color: `${r.accent}80`, fontWeight: '400', margin: '0 0 6px 0' }}>{r.sub}</p>
            <div style={{ filter: 'blur(4px)', userSelect: 'none', WebkitUserSelect: 'none', pointerEvents: 'none' }}>
              <div style={{ height: '20px', borderRadius: '3px', background: `${r.accent}10` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DomainInsights = () => {
  const { user, isPremium: sessionIsPremium } = useSession();  // Get user AND premium status from session context
  
  // Use session context isPremium (reactive) with localStorage fallback
  const isPremium = sessionIsPremium || (() => {
    try {
      return localStorage.getItem('is_premium') === 'true' || localStorage.getItem('isPremium') === 'true';
    } catch { return false; }
  })();
  
  // Prioritize session user data over localStorage (prevents stale data)
  const [birthDetails, setBirthDetails] = useState(() => {
    // First check if we have user data from session
    if (user?.birth_data) {
      console.log('✅ Using birth_data from session context:', user.birth_data.name);
      return {
        name: user.birth_data.name || 'User',
        year: user.birth_data.year,
        month: user.birth_data.month,
        day: user.birth_data.day,
        hour: user.birth_data.hour || 12,
        minute: user.birth_data.minute || 0,
        latitude: user.birth_data.latitude,
        longitude: user.birth_data.longitude,
        place: user.birth_data.place,
        gender: user.birth_data.gender || 'male'
      };
    }
    // Fallback to localStorage
    return getInitialBirthDetails();
  });
  
  // Update birth details when user changes
  useEffect(() => {
    if (user?.birth_data) {
      console.log('✅ Updating birth_data from session:', user.birth_data.name);
      setBirthDetails({
        name: user.birth_data.name || 'User',
        year: user.birth_data.year,
        month: user.birth_data.month,
        day: user.birth_data.day,
        hour: user.birth_data.hour || 12,
        minute: user.birth_data.minute || 0,
        latitude: user.birth_data.latitude,
        longitude: user.birth_data.longitude,
        place: user.birth_data.place,
        gender: user.birth_data.gender || 'male'
      });
    }
  }, [user]);

  useEffect(() => {
    trackPageView('life_insights');
    trackEvent('life_insights_viewed', { is_premium: localStorage.getItem('isPremium') === 'true' });
    rlog('life_insights_mount', {
      is_premium: localStorage.getItem('isPremium') === 'true',
      plan_id: localStorage.getItem('plan_id'),
      plan_unlocks: localStorage.getItem('plan_unlocks'),
      has_cached_report: !!localStorage.getItem(`detailed_report_${localStorage.getItem('user_id') || 'guest'}`),
      content_version: (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}').life_insights; } catch { return null; } })(),
    });
  }, []);
  
  const [report, setReport] = useState(() => {
    // Try to get report from cache immediately on mount
    try {
      const userId = localStorage.getItem('user_id') || localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || localStorage.getItem('session_id') || 'guest';
      const cached = localStorage.getItem(`detailed_report_${userId}`);
      if (cached) {
        const p = JSON.parse(cached);
        if (p?.header) {
          // Validate cached report name matches current user
          const birthRaw = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
          if (birthRaw) {
            try {
              const bd = JSON.parse(birthRaw);
              const currentName = (bd.name || '').toLowerCase().trim();
              const cachedName = (p.header.name || '').toLowerCase().trim();
              if (currentName && cachedName && !currentName.split(/\s+/).some(part => part.length > 2 && cachedName.includes(part))) {
                console.log(`[DomainInsights] Stale report cache (${cachedName} != ${currentName}). Clearing...`);
                localStorage.removeItem(`detailed_report_${userId}`);
                return null;
              }
            } catch(e) {}
          }
          console.log('[DomainInsights] Instant load from cache');
          return p;
        }
      }
    } catch (e) { /* ignore */ }
    return null;
  });
  const [loading, setLoading] = useState(() => {
    // Don't show loading if we have cached report
    try {
      const userId = localStorage.getItem('user_id') || localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || localStorage.getItem('session_id') || 'guest';
      const cached = localStorage.getItem(`detailed_report_${userId}`);
      if (cached) {
        const p = JSON.parse(cached);
        return !p?.header; // If we have cached header, don't show loading
      }
    } catch (e) { /* ignore */ }
    return true;
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [activeDomain, setActiveDomain] = useState(1);
  const [selectedLanguage, setSelectedLanguage] = useState('english');  // Default to English
  const [aiContentLoading, setAiContentLoading] = useState(() => {
    // If user is premium and no AI content cached, start in loading state
    // This prevents flashing old algorithmic content before AI content loads
    try {
      const isPrem = localStorage.getItem('is_premium') === 'true' || localStorage.getItem('isPremium') === 'true';
      if (isPrem) {
        const stored = localStorage.getItem('ai_content_data');
        if (!stored) return true; // No cache → show loading, not old content
      }
    } catch(e) { /* ignore */ }
    return false;
  });
  
  // Track if AI fetch has completed (success or failure) - prevents showing algorithm content prematurely
  const [aiFetchDone, setAiFetchDone] = useState(false);
  const [aiFetchFailed, setAiFetchFailed] = useState(false);
  
  // For premium users: keep loading shimmer until AI content arrives or fetch definitively fails
  // No safety timeout - premium users should NEVER see old algorithm content
  // The fetchAiContent function has its own error handling that sets aiContentLoading=false on failure
  
  // NEW: Enhanced predictions from 17 algorithms - load from cache immediately
  const [enhancedData, setEnhancedData] = useState(() => {
    try {
      // Get current person's name from birthDetails for validation
      const birthRaw = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      const currentName = birthRaw ? (JSON.parse(birthRaw).name || '').toLowerCase().trim() : '';
      
      // Try unified_story_data first (has domain_insights) — validate name match
      const unified = localStorage.getItem('unified_story_data');
      if (unified) {
        const ud = JSON.parse(unified);
        const cachedName = (ud.user_info?.name || '').toLowerCase().trim();
        if (currentName && cachedName && currentName !== cachedName) {
          console.log(`[DomainInsights] Name mismatch: cached="${cachedName}" current="${currentName}". Clearing stale caches.`);
          localStorage.removeItem('unified_story_data');
          localStorage.removeItem('domain_insights_cache');
          localStorage.removeItem('ai_content_data');
          localStorage.removeItem('ai_story_data');
          localStorage.removeItem('health_timeline_data');
          return null;
        }
        if (ud.domain_insights) {
          console.log('[DomainInsights] Enhanced data loaded from unified_story_data');
          return { insights: { enhanced_domains: ud.domain_insights } };
        }
      }
      // Try domain_insights_cache
      const diCache = localStorage.getItem('domain_insights_cache');
      if (diCache) {
        console.log('[DomainInsights] Enhanced data loaded from domain_insights_cache');
        return { insights: { enhanced_domains: JSON.parse(diCache) } };
      }
    } catch (e) { /* ignore */ }
    return null;
  });
  const [loadingEnhanced, setLoadingEnhanced] = useState(false);
  
  // NEW: Full health timeline data with gender-specific profiles - try cache first
  const [healthTimelineData, setHealthTimelineData] = useState(() => {
    try {
      const stored = localStorage.getItem('health_timeline_data');
      if (stored) {
        console.log('[DomainInsights] Health timeline loaded from cache');
        return JSON.parse(stored);
      }
    } catch (e) { /* ignore */ }
    return null;
  });
  
  // NEW: AI-generated content from batch API
  const [aiContent, setAiContent] = useState(() => {
    try {
      const stored = localStorage.getItem('ai_content_data');
      const parsed = stored ? JSON.parse(stored) : null;
      // Name validation — don't use cached data from different person
      if (parsed) {
        const birthRaw = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
        if (birthRaw) {
          const currentName = (JSON.parse(birthRaw).name || '').toLowerCase().trim();
          const cachedName = (parsed.user_info?.name || '').toLowerCase().trim();
          if (currentName && cachedName && currentName !== cachedName) {
            console.log('[DomainInsights] AI Content name mismatch, discarding');
            return null;
          }
        }
      }
      console.log('[DomainInsights] AI Content loaded:', parsed ? 'Yes' : 'No');
      return parsed;
    } catch (e) {
      console.log('[DomainInsights] AI Content parse error:', e);
      return null;
    }
  });
  
  // Premium status
  // isPremium now comes from useSession above (reactive)

  
  // NEW: Unified story data with universal profiles (wealth, marriage)
  const [unifiedStoryData, setUnifiedStoryData] = useState(() => {
    try {
      const stored = localStorage.getItem('unified_story_data');
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      // Name validation — don't use cached data from different person
      const birthRaw = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (birthRaw) {
        const currentName = (JSON.parse(birthRaw).name || '').toLowerCase().trim();
        const cachedName = (parsed.user_info?.name || '').toLowerCase().trim();
        if (currentName && cachedName && currentName !== cachedName) {
          return null;
        }
      }
      return parsed;
    } catch (e) {
      return null;
    }
  });
  
  // Fetch birth details from API - only if not already loaded from cache
  useEffect(() => {
    // If birthDetails already have year from cache init, skip API
    if (birthDetails?.year && birthDetails?.month && birthDetails?.day) {
      console.log('[DomainInsights] Birth details already loaded from cache, skipping API');
      return;
    }
    
    const fetchBirthDetailsFromAPI = async () => {
      // FIRST: Check localStorage before hitting API (prevents overwriting fresh onboarding data)
      const existingLocal = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (existingLocal) {
        try {
          const parsed = JSON.parse(existingLocal);
          if (parsed.name && (parsed.year || parsed.dateOfBirth)) {
            console.log('[DomainInsights] Using localStorage birth data for:', parsed.name);
            const dateParts = (parsed.dateOfBirth || '').split(/[-\/]/);
            let year = parsed.year, month = parsed.month, day = parsed.day;
            if (!year && dateParts.length === 3) {
              if (dateParts[0].length === 4) { year = parseInt(dateParts[0]); month = parseInt(dateParts[1]); day = parseInt(dateParts[2]); }
              else { day = parseInt(dateParts[0]); month = parseInt(dateParts[1]); year = parseInt(dateParts[2]); }
            }
            let hour = parsed.hour || 12, minute = parsed.minute || 0;
            const timeStr = parsed.timeOfBirth;
            if (timeStr) { const p = parseTimeString(timeStr); hour = p.hour; minute = p.minute; }
            setBirthDetails({
              name: parsed.name || 'User', year: parseInt(year), month: parseInt(month) || 1,
              day: parseInt(day) || 1, hour: parseInt(hour), minute: parseInt(minute),
              place: parsed.place || parsed.placeOfBirth || 'India', gender: parsed.gender || 'unknown',
              latitude: parseFloat(parsed.latitude) || 28.6139, longitude: parseFloat(parsed.longitude) || 77.209
            });
            return;
          }
        } catch (e) { /* ignore */ }
      }
      
      const userId = localStorage.getItem('user_id') || localStorage.getItem('firebase_uid');
      const sessionId = localStorage.getItem('session_id');
      
      console.log('[DomainInsights] Fetching birth details. userId:', userId, 'sessionId:', sessionId);
      
      if (!userId && !sessionId) {
        console.log('[DomainInsights] No userId or sessionId found');
        return;
      }
      
      try {
        // Try multiple endpoints in order of preference
        let data = null;
        
        // 1. Try by user_id first (most reliable for logged-in users)
        // Railway uses /api/birth-details/user/{user_id} instead of /api/birth-details/load-by-user/{user_id}
        if (userId) {
          console.log('[DomainInsights] Trying user endpoint...');
          const res1 = await fetch(`${BACKEND_URL}/api/birth-details/user/${userId}`);
          data = await res1.json();
          console.log('[DomainInsights] user endpoint response:', data);
        }
        
        // 2. If not found, try by session_id
        if ((!data || !data.success) && sessionId) {
          console.log('[DomainInsights] Trying load by session_id...');
          const res2 = await fetch(`${BACKEND_URL}/api/birth-details/load/${sessionId}`);
          data = await res2.json();
          console.log('[DomainInsights] load by session response:', data);
        }
        
        // 3. Fallback to latest
        if (!data || !data.success) {
          console.log('[DomainInsights] Trying load-latest endpoint...');
          const res3 = await fetch(`${BACKEND_URL}/api/birth-details/load-latest`);
          data = await res3.json();
          console.log('[DomainInsights] load-latest response:', data);
        }
        
        const bd = data?.birth_details || data?.data;
        if (data?.success && bd) {
          // Parse time from "12:17 PM" format
          let hour = 12, minute = 0;
          const timeStr = bd.timeOfBirth || bd.time_of_birth;
          if (timeStr) {
            const parsed = parseTimeString(timeStr);
            hour = parsed.hour;
            minute = parsed.minute;
            console.log('[DomainInsights] Parsed time:', timeStr, '->', hour, ':', minute);
          }
          
          // Parse date from "14-05-1997" format
          let year, month, day;
          const dateStr = bd.dateOfBirth || bd.date_of_birth;
          if (dateStr) {
            const parsed = parseDateString(dateStr);
            if (parsed) {
              year = parsed.year;
              month = parsed.month;
              day = parsed.day;
              console.log('[DomainInsights] Parsed date:', dateStr, '->', day, '/', month, '/', year);
            }
          }
          
          const newBirthDetails = {
            name: bd.name || 'User',
            year: year || bd.year,
            month: month || bd.month || 1,
            day: day || bd.day || 1,
            hour: hour,
            minute: minute,
            place: bd.place || bd.placeOfBirth || 'India',
            gender: bd.gender || 'unknown',
            latitude: bd.latitude || 28.6139,
            longitude: bd.longitude || 77.209
          };
          
          console.log('[DomainInsights] Final birth details:', newBirthDetails);
          setBirthDetails(newBirthDetails);
          
          // Update localStorage with correct data
          const existingData = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
          existingData.user_info = { ...existingData.user_info, ...newBirthDetails };
          localStorage.setItem('unified_story_data', JSON.stringify(existingData));
          setUnifiedStoryData(existingData);
        } else {
          console.log('[DomainInsights] No birth details found from API');
        }
      } catch (e) {
        console.error('[DomainInsights] Failed to fetch birth details from API:', e);
      }
    };
    
    // Always fetch from API to ensure we have latest data
    fetchBirthDetailsFromAPI();
  }, []);
  
  // Effect to sync unifiedStoryData from localStorage when it changes
  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const stored = localStorage.getItem('unified_story_data');
        if (stored) {
          setUnifiedStoryData(JSON.parse(stored));
        }
      } catch (e) {
        console.error('Error syncing unified_story_data:', e);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Intro animation state - to show users sections are expandable
  const [introOpen, setIntroOpen] = useState(null);
  const [introAnimationPlayed, setIntroAnimationPlayed] = useState(() => {
    return sessionStorage.getItem('insightsIntroPlayed') === 'true';
  });

  // Intro animation effect - briefly expand first section
  useEffect(() => {
    if (!introAnimationPlayed && report && !loading && !generating) {
      const timer1 = setTimeout(() => {
        setIntroOpen(true);
      }, 600);
      
      const timer2 = setTimeout(() => {
        setIntroOpen(false);
        setIntroAnimationPlayed(true);
        sessionStorage.setItem('insightsIntroPlayed', 'true');
      }, 2200);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [introAnimationPlayed, report, loading, generating]);

  const generateReport = async (forceRefresh = false) => {
    // CRITICAL: Validate birth details have required fields
    if (!birthDetails?.year || !birthDetails?.month || !birthDetails?.day) {
      console.log('[DomainInsights] Cannot generate report - missing birth details:', birthDetails);
      rlog('life_insights_generate_skipped', { reason: 'missing_birth_details' });
      setLoading(false);
      return;
    }
    const userId = localStorage.getItem('user_id') || localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || localStorage.getItem('session_id') || 'default';
    
    // FAST PATH: If we have a cached report, use it immediately
    if (!forceRefresh) {
      const cached = localStorage.getItem(`detailed_report_${userId}`);
      if (cached) { 
        try { 
          const p = JSON.parse(cached); 
          if (p?.header) { 
            console.log('[DomainInsights] INSTANT: Using cached report');
            rlog('life_insights_cache_hit', { user_id: userId, has_lagna: !!p.header.lagna });
            setReport(p); 
            setLoading(false); 
            return; 
          } 
        } catch (e) { /* ignore */ } 
      }
    }
    
    // If no cached report, generate new one
    setGenerating(true); setLoading(false);
    console.log('[DomainInsights] Generating new report with birth details:', birthDetails);
    rlog('life_insights_generate_start', {
      user_id: userId,
      force_refresh: forceRefresh,
      content_version: (() => { try { return JSON.parse(localStorage.getItem('content_versions') || '{}').life_insights; } catch { return null; } })(),
    });
    const genStart = Date.now();
    try {
      let lagna = null, moonSign = null, currentMd = null, nextMd = null, allMahadashas = [];
      try { const ud = JSON.parse(localStorage.getItem('unified_story_data') || '{}'); allMahadashas = ud.all_mahadashas || []; const hd = ud.kernel?.health || {}; lagna = hd.lagna || hd.ascendant; moonSign = hd.moon_sign || hd.rashi; for (const md of allMahadashas) { if (md.status === 'current' || md.is_current) currentMd = md.lord; else if (md.status === 'future' && !nextMd) nextMd = md.lord; } } catch (e) { /* ignore */ }
      
      // Double-check required fields before API call
      if (!birthDetails.year || !birthDetails.month || !birthDetails.day) {
        console.error('[DomainInsights] Missing required birth data - aborting API call');
        setError('Missing birth details');
        setGenerating(false);
        return;
      }
      
      // Use the correct API endpoint
      const res = await fetch(`${BACKEND_URL}/api/report/generate-detailed`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: userId, name: birthDetails.name || 'User', year: parseInt(birthDetails.year), month: parseInt(birthDetails.month), day: parseInt(birthDetails.day), hour: parseInt(birthDetails.hour) || 12, minute: parseInt(birthDetails.minute) || 0, place: birthDetails.place || 'India', gender: birthDetails.gender || 'unknown', latitude: parseFloat(birthDetails.latitude) || 28.6139, longitude: parseFloat(birthDetails.longitude) || 77.209, force_regenerate: forceRefresh }) });
      const data = await res.json();
      if (data.success && data.report) { 
        setReport(data.report); 
        localStorage.setItem(`detailed_report_${userId}`, JSON.stringify(data.report)); 
        // Create birth hash for cache validation
        const computedBirthHash = `${birthDetails.name}_${birthDetails.year}_${birthDetails.month}_${birthDetails.day}_${birthDetails.hour || 12}`;
        localStorage.setItem(`birth_hash_${userId}`, computedBirthHash);
        console.log('[DomainInsights] New report generated with Lagna:', data.report?.header?.lagna);
        rlog('life_insights_generate_done', {
          user_id: userId,
          elapsed_ms: Date.now() - genStart,
          force_refresh: forceRefresh,
          http_status: res.status,
          lagna: data.report?.header?.lagna,
          algorithm_version: data.report?.header?.algorithm_version || data.report?.algorithm_version,
          domains_count: Object.keys(data.report?.domains || {}).length,
        });
      }
      else {
        setError('Failed to generate report');
        rlog('life_insights_generate_failed', { user_id: userId, elapsed_ms: Date.now() - genStart, http_status: res.status, message: data?.message });
      }
    } catch (e) {
      console.error('[DomainInsights] Report generation error:', e);
      rlog('life_insights_generate_exception', { user_id: userId, elapsed_ms: Date.now() - genStart, error: e?.message });
      if (!report?.header) setError('Failed');
    }
    finally { setGenerating(false); }
  };

  // NEW: Fetch enhanced predictions from 17 algorithms
  const fetchEnhancedData = async () => {
    // Validate required birth details
    if (!birthDetails?.year || !birthDetails?.month || !birthDetails?.day) {
      console.log('[DomainInsights] Cannot fetch enhanced data - missing birth details');
      return;
    }
    const userId = localStorage.getItem('user_id') || localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || localStorage.getItem('session_id') || 'default';
    
    setLoadingEnhanced(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/dashboard/enhanced-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          name: birthDetails.name || 'User',
          year: parseInt(birthDetails.year),
          month: parseInt(birthDetails.month),
          day: parseInt(birthDetails.day),
          hour: parseInt(birthDetails.hour) || 12,
          minute: parseInt(birthDetails.minute) || 0,
          place: birthDetails.place || 'India',
          latitude: parseFloat(birthDetails.latitude) || 28.6139,
          longitude: parseFloat(birthDetails.longitude) || 77.209,
          gender: birthDetails.gender || 'unknown',
          force_refresh: false,
        }),
      });
      const data = await res.json();
      if (data.success) {
        console.log('[DomainInsights] Enhanced data loaded from', data.insights?.meta?.engines_used?.length, 'engines');
        setEnhancedData(data.insights);
      }
    } catch (e) {
      console.error('[DomainInsights] Enhanced data fetch error:', e);
    } finally {
      setLoadingEnhanced(false);
    }
  };

  // NEW: Fetch full health timeline with gender-specific profiles
  const fetchHealthTimeline = async () => {
    if (!birthDetails?.year || !birthDetails?.month || !birthDetails?.day) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/health-timeline/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: birthDetails.name || 'User',
          year: parseInt(birthDetails.year),
          month: parseInt(birthDetails.month),
          day: parseInt(birthDetails.day),
          hour: parseInt(birthDetails.hour) || 12,
          minute: parseInt(birthDetails.minute) || 0,
          latitude: parseFloat(birthDetails.latitude) || 28.6139,
          longitude: parseFloat(birthDetails.longitude) || 77.209,
          gender: birthDetails.gender || 'unknown',
        }),
      });
      const data = await res.json();
      if (data.success && data.data?.enhanced) {
        console.log('[DomainInsights] Health timeline loaded with gender profile:', data.data.enhanced.male_health_profile?.constitution_type || data.data.enhanced.female_health_profile?.constitution_type);
        setHealthTimelineData(data.data.enhanced);
      }
    } catch (e) {
      console.error('[DomainInsights] Health timeline fetch error:', e);
    }
  };

  // NEW: Auto-fetch AI content if not available - uses FAST endpoint
  const fetchAiContent = async () => {
    if (!birthDetails?.year || !birthDetails?.month || !birthDetails?.day) return;
    
    const REQUIRED_DOMAINS = ['health', 'career', 'wealth', 'education', 'marriage', 'family', 'travel', 'spiritual'];
    
    // Validate cache completeness: all 8 domains must have at least 1 non-empty section
    const isCacheComplete = (insights) => {
      if (!insights) return false;
      for (const domain of REQUIRED_DOMAINS) {
        const data = insights[domain];
        if (!data || typeof data !== 'object') return false;
        const hasContent = Object.values(data).some(v => typeof v === 'string' && v.length > 10);
        if (!hasContent) return false;
      }
      return true;
    };
    
    // Check per-language cache FIRST for instant switching — with name + completeness validation
    try {
      const cachedLang = localStorage.getItem(`ai_content_${selectedLanguage}`);
      if (cachedLang) {
        const parsed = JSON.parse(cachedLang);
        if (parsed?.life_insights) {
          // Check completeness first — if any domain is empty, force refetch
          if (!isCacheComplete(parsed.life_insights)) {
            console.log(`[DomainInsights] Incomplete cache detected (missing domain AI). Clearing for refetch...`);
            ['english', 'hindi', 'hinglish'].forEach(l => localStorage.removeItem(`ai_content_${l}`));
            localStorage.removeItem('ai_content_data');
          } else {
            // Validate cached content matches current user's name
            const currentName = (birthDetails?.name || '').toLowerCase().trim();
            const firstDomain = Object.values(parsed.life_insights)[0];
            const firstSection = firstDomain ? Object.values(firstDomain)[0] : '';
            const contentStr = typeof firstSection === 'string' ? firstSection.toLowerCase() : '';
            
            if (currentName && contentStr.length > 50) {
              const nameParts = currentName.split(/\s+/).filter(p => p.length > 2);
              const hasCurrentName = nameParts.some(part => contentStr.includes(part));
              if (!hasCurrentName) {
                console.log(`[DomainInsights] Stale cache detected (wrong user name). Clearing...`);
                ['english', 'hindi', 'hinglish'].forEach(l => localStorage.removeItem(`ai_content_${l}`));
                localStorage.removeItem('ai_content_data');
              } else {
                console.log(`[DomainInsights] Instant cache hit for ${selectedLanguage}`);
                setAiContent({ ai_generated: true, life_insights: parsed.life_insights, language: selectedLanguage });
                return;
              }
            } else {
              console.log(`[DomainInsights] Instant cache hit for ${selectedLanguage}`);
              setAiContent({ ai_generated: true, life_insights: parsed.life_insights, language: selectedLanguage });
              return;
            }
          }
        }
      }
    } catch(e) { /* ignore */ }
    
    // Fallback: Check generic ai_content_data (stored by payment flow)
    try {
      const genericCache = localStorage.getItem('ai_content_data');
      if (genericCache) {
        const parsed = JSON.parse(genericCache);
        if (parsed?.life_insights && (parsed.language === selectedLanguage || selectedLanguage === 'english')) {
          const REQUIRED_DOMAINS = ['health', 'career', 'wealth', 'education', 'marriage', 'family', 'travel', 'spiritual'];
          const allComplete = REQUIRED_DOMAINS.every(d => {
            const data = parsed.life_insights[d];
            return data && typeof data === 'object' && Object.values(data).some(v => typeof v === 'string' && v.length > 10);
          });
          if (allComplete) {
            console.log('[DomainInsights] Fallback cache hit from ai_content_data');
            localStorage.setItem(`ai_content_${selectedLanguage}`, genericCache);
            setAiContent({ ai_generated: true, life_insights: parsed.life_insights, language: selectedLanguage });
            return;
          }
        }
      }
    } catch(e) { /* ignore */ }
    
    console.log('[DomainInsights] Fetching AI content for', selectedLanguage);
    
    // Check if generation is already in progress from payment flow
    const genInProgress = localStorage.getItem('ai_generation_in_progress') === 'true';
    if (genInProgress && selectedLanguage === 'english') {
      console.log('[DomainInsights] Generation in progress from payment — waiting...');
      // Poll for the result instead of making a duplicate API call
      for (let wait = 0; wait < 20; wait++) {
        await new Promise(r => setTimeout(r, 3000)); // Check every 3s
        try {
          const cached = localStorage.getItem('ai_content_english');
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed?.life_insights) {
              console.log('[DomainInsights] Payment generation completed — using result');
              setAiContent({ ai_generated: true, life_insights: parsed.life_insights, language: 'english' });
              setAiContentLoading(false);
              return;
            }
          }
        } catch(e) { /* ignore */ }
        // Check if generation finished (flag cleared)
        if (localStorage.getItem('ai_generation_in_progress') !== 'true') break;
      }
      // If still nothing after waiting, fall through to fetch
      console.log('[DomainInsights] Wait timeout — fetching directly');
    }
    
    setAiContentLoading(true);
    setAiFetchFailed(false);
    
    // Retry up to 3 times for resilience
    const MAX_RETRIES = 3;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const aiResponse = await fetch(`${BACKEND_URL}/api/ai-story/life-insights-fast`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            birth_data: {
              name: birthDetails.name || 'User',
              year: parseInt(birthDetails.year),
              month: parseInt(birthDetails.month),
              day: parseInt(birthDetails.day),
              hour: parseInt(birthDetails.hour) || 12,
              minute: parseInt(birthDetails.minute) || 0,
              latitude: parseFloat(birthDetails.latitude) || 28.6139,
              longitude: parseFloat(birthDetails.longitude) || 77.209,
              gender: birthDetails.gender || 'male'
            },
            language: selectedLanguage
          })
        });
        
        if (aiResponse.ok) {
          const aiResult = await aiResponse.json();
          if (aiResult.success && aiResult.life_insights) {
            console.log('[DomainInsights] AI Life Insights fetched!', Object.keys(aiResult.life_insights));
            const newAiContent = {
              ai_generated: true,
              life_insights: aiResult.life_insights,
              language: selectedLanguage,
              cached_name: birthDetails?.name || 'User'
            };
            localStorage.setItem(`ai_content_${selectedLanguage}`, JSON.stringify(newAiContent));
            localStorage.setItem('ai_content_data', JSON.stringify(newAiContent));
            setAiContent(newAiContent);
            
            // Background pre-fetch other languages for instant switching
            const otherLangs = ['english', 'hindi', 'hinglish'].filter(l => l !== selectedLanguage);
            otherLangs.forEach(lang => {
              if (!localStorage.getItem(`ai_content_${lang}`)) {
                fetch(`${BACKEND_URL}/api/ai-story/life-insights-fast`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    birth_data: {
                      name: birthDetails.name || 'User',
                      year: parseInt(birthDetails.year),
                      month: parseInt(birthDetails.month),
                      day: parseInt(birthDetails.day),
                      hour: parseInt(birthDetails.hour) || 12,
                      minute: parseInt(birthDetails.minute) || 0,
                      latitude: parseFloat(birthDetails.latitude) || 28.6139,
                      longitude: parseFloat(birthDetails.longitude) || 77.209,
                      gender: birthDetails.gender || 'male'
                    },
                    language: lang
                  })
                }).then(r => r.json()).then(res => {
                  if (res.success && res.life_insights) {
                    localStorage.setItem(`ai_content_${lang}`, JSON.stringify({
                      ai_generated: true, life_insights: res.life_insights, language: lang
                    }));
                    console.log(`[DomainInsights] Pre-cached ${lang}`);
                  }
                }).catch(() => {});
              }
            });
            // Success — exit retry loop
            setAiContentLoading(false);
            setAiFetchDone(true);
            return;
          }
        }
        // If response not ok or no insights, retry
        if (attempt < MAX_RETRIES) {
          console.log(`[DomainInsights] Attempt ${attempt} failed, retrying in 3s...`);
          await new Promise(r => setTimeout(r, 3000));
        }
      } catch (e) {
        console.error(`[DomainInsights] AI fetch attempt ${attempt} error:`, e);
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 3000));
        }
      }
    }
    // All retries failed — set failed state but DON'T set aiFetchDone (keeps shimmer for premium)
    console.error('[DomainInsights] All AI fetch retries exhausted');
    setAiContentLoading(false);
    setAiFetchFailed(true);
    setAiFetchDone(true);
  };

  // AI Insights fetch - DISABLED until budget is added
  // const fetchAiInsights = async () => { ... };

  useEffect(() => { 
    // Skip if missing birth details
    if (!birthDetails?.year || !birthDetails?.month || !birthDetails?.day) { 
      setLoading(false); 
      return; 
    }
    // SKIP API call if we already have report from cache initialization
    if (report?.header) {
      console.log('[DomainInsights] Already have cached report, skipping API call');
      setLoading(false);
      setError(null); // Clear any stale error
      return;
    }
    generateReport(); 
  }, [birthDetails]);
  
  // Fetch enhanced data and health timeline after report is loaded
  useEffect(() => { 
    if (report && birthDetails?.year && birthDetails?.month && birthDetails?.day) { 
      // SKIP if we already have enhanced data from cache initialization
      if (enhancedData?.insights?.enhanced_domains) {
        console.log('[DomainInsights] Already have cached enhanced data, skipping API');
      } else {
        fetchEnhancedData(); 
      }
      // Skip health timeline if already loaded from cache
      if (healthTimelineData) {
        console.log('[DomainInsights] Already have cached health timeline, skipping API');
      } else {
        fetchHealthTimeline();
      }
    } 
  }, [report, birthDetails]);

  // Auto-fetch AI content - refetch when language changes or content is missing
  useEffect(() => {
    if (report && birthDetails?.year) {
      // Always call fetchAiContent on language change - it checks per-language cache internally
      fetchAiContent();
    } else if (!report || !birthDetails?.year) {
      // Can't fetch AI content without report/birth details — stop loading shimmer
      setAiContentLoading(false);
    }
  }, [report, birthDetails?.year, selectedLanguage]);
  
  const refresh = async () => { 
    const userId = localStorage.getItem('user_id') || localStorage.getItem('taraanaya_device_id') || localStorage.getItem('device_id') || localStorage.getItem('session_id') || 'default'; 
    
    // Re-read birth details from localStorage to get latest correct values
    const freshBirthDetails = getInitialBirthDetails() || birthDetails;
    if (freshBirthDetails && JSON.stringify(freshBirthDetails) !== JSON.stringify(birthDetails)) {
      console.log('[DomainInsights] 🔄 Updated birth details from localStorage');
      setBirthDetails(freshBirthDetails);
    }
    const bd = freshBirthDetails || birthDetails;
    
    // Clear ALL cached data including AI content
    localStorage.removeItem(`detailed_report_${userId}`);
    localStorage.removeItem('ai_content_data');
    localStorage.removeItem('ai_story_data');
    localStorage.removeItem('ai_life_insights');
    localStorage.removeItem('domain_insights_cache');
    localStorage.removeItem('health_timeline_data');
    
    try { 
      await fetch(`${BACKEND_URL}/api/report/clear/${userId}`, { method: 'DELETE' }); 
      await fetch(`${BACKEND_URL}/api/dashboard/enhanced-insights/${userId}`, { method: 'DELETE' }); 
    } catch (e) { /* ignore */ } 
    
    // Clear states
    setReport(null); 
    setEnhancedData(null); 
    setHealthTimelineData(null);
    setAiContent(null);
    
    // Step 1: Regenerate AI content using FAST endpoint (single GPT call)
    setGenerating(true);
    try {
      console.log('🔄 Regenerating AI content (fast)...');
      const aiResponse = await fetch(`${BACKEND_URL}/api/ai-story/life-insights-fast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          birth_data: {
            name: bd.name || 'User',
            year: parseInt(bd.year),
            month: parseInt(bd.month),
            day: parseInt(bd.day),
            hour: parseInt(bd.hour) || 12,
            minute: parseInt(bd.minute) || 0,
            latitude: parseFloat(bd.latitude) || 28.6139,
            longitude: parseFloat(bd.longitude) || 77.209,
            gender: bd.gender || 'male'
          },
          language: selectedLanguage,
          force_refresh: true
        })
      });
      
      if (aiResponse.ok) {
        const aiResult = await aiResponse.json();
        if (aiResult.success && aiResult.life_insights) {
          console.log('✅ AI content regenerated!', Object.keys(aiResult.life_insights));
          const newAiContent = {
            ai_generated: true,
            life_insights: aiResult.life_insights,
            language: selectedLanguage
          };
          localStorage.setItem('ai_content_data', JSON.stringify(newAiContent));
          setAiContent(newAiContent);
        }
      }
    } catch (e) {
      console.log('AI regeneration error:', e.message);
    }
    
    // Step 2: Then generate detailed report
    await generateReport(true); 
  };

  const bg = { 
    background: 'radial-gradient(ellipse at 50% 0%, #1a2332 0%, #0a0e17 100%)', 
    height: '100vh', 
    position: 'fixed', 
    inset: 0, 
    overflow: 'hidden' 
  };

  if (loading && !report) {
    // Only show loading spinner if we truly have NO data at all
    return <div style={{ ...bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ScrollingPlanets activeDomain={1} /><motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }} style={{ width: '20px', height: '20px', border: '2px solid rgba(116,179,255,0.15)', borderTopColor: '#74B3FF', borderRadius: '50%' }} /></div>;
  }
  if (!birthDetails?.year || !birthDetails?.month || !birthDetails?.day) return <div style={{ ...bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', textAlign: 'center' }}><ScrollingPlanets activeDomain={1} /><p style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', color: 'rgba(255,255,255,0.7)', marginBottom: '8px', fontWeight: '400' }}>Complete your birth details</p><p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.4)', fontWeight: '400' }}>Go to Profile</p></div>;
  if (generating) {
    // Don't block forever - show a non-blocking indicator
    // The report will load in background and update the UI
    console.log('[DomainInsights] Report generating in background...');
  }
  if (error) return <div style={{ ...bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}><ScrollingPlanets activeDomain={1} /><p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.6)', marginBottom: '16px', fontWeight: '400' }}>{error}</p><button onClick={() => generateReport(true)} style={{ padding: '12px 24px', borderRadius: '8px', fontSize: 'clamp(11px, 3vw, 13px)', background: 'rgba(116,179,255,0.1)', color: '#74B3FF', border: 'none', cursor: 'pointer', fontWeight: '400' }}>Retry</button></div>;
  if (!report) return <div style={{ ...bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ScrollingPlanets activeDomain={1} /></div>;

  const h = report.header || {};
  const d1 = report.domain_1_health || {};
  const d2 = report.domain_2_career || {};
  const d3 = report.domain_3_wealth || {};
  const d4 = report.domain_4_education || {};
  const d5 = report.domain_5_marriage || {};
  const d6 = report.domain_6_family || {};
  const d7 = report.domain_7_travel || {};
  const d8 = report.domain_8_spiritual || {};
  
  // Safe access to enhanced domains - prevents crash when data is null/loading
  const ed = enhancedData?.enhanced_domains || {};
  const edHealth = ed.health?.sections || {};
  const edCareer = ed.career?.sections || {};
  const edWealth = ed.wealth?.sections || {};
  const edEducation = ed.education?.sections || {};
  const edMarriage = ed.marriage?.sections || {};
  const edFamily = ed.family?.sections || {};
  const edTravel = ed.travel?.sections || {};
  const edSpiritual = ed.spiritual?.sections || {};

  return (
    <div style={bg}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: '112px', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
      <ScrollingPlanets activeDomain={activeDomain} />
      
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50, paddingLeft: '14px', paddingRight: '14px', paddingBottom: '12px', paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))', background: 'rgba(10,14,23,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(116,179,255,0.05)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
          <div style={{ flexShrink: 0 }}>
            <h1 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(14px, 4vw, 18px)',
              color: '#F3E5AB',
              margin: 0,
              fontWeight: 300,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
            }}>Life Insights</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button data-testid="insights-refresh-btn" onClick={refresh} style={{ 
              height: '28px', 
              width: '28px',
              borderRadius: '14px', 
              background: generating ? 'rgba(116,179,255,0.15)' : 'rgba(116,179,255,0.08)', 
              border: '1px solid rgba(116,179,255,0.2)', 
              cursor: generating ? 'wait' : 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: 0,
              flexShrink: 0,
              opacity: generating ? 0.6 : 1
            }}>
              {generating ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                  <RefreshCw style={{ width: '13px', height: '13px', color: 'rgba(116,179,255,0.8)' }} />
                </motion.div>
              ) : (
                <RefreshCw style={{ width: '13px', height: '13px', color: 'rgba(116,179,255,0.8)' }} />
              )}
            </button>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 10 }}>
        
        {/* Profile */}
        <div style={{ padding: '28px 20px', borderBottom: '1px solid rgba(116,179,255,0.06)' }}>
          <p style={{ fontSize: 'clamp(14px, 4vw, 18px)', color: 'rgba(255,255,255,0.95)', marginBottom: '6px', fontWeight: '500' }}>{birthDetails.name || h.name}</p>
          <p style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.45)', fontWeight: '400' }}>{birthDetails.day} {MONTHS[birthDetails.month]} {birthDetails.year} · {birthDetails.hour}:{String(birthDetails.minute).padStart(2, '0')} · {birthDetails.place}</p>
        </div>

        {/* Core Profile */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid rgba(116,179,255,0.06)' }}>
          {[{ l: 'Rising', v: h.lagna || '—' }, { l: 'Moon', v: h.moon_sign || '—' }, { l: 'Phase', v: h.current_mahadasha || '—' }, { l: 'Next', v: h.next_mahadasha || '—' }].map((item, i) => (
            <div key={i} style={{ padding: '16px 12px', textAlign: 'center', borderRight: i < 3 ? '1px solid rgba(116,179,255,0.06)' : 'none' }}>
              <p style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(116,179,255,0.5)', marginBottom: '4px', fontWeight: '500', letterSpacing: '0.05em' }}>{item.l.toUpperCase()}</p>
              <p style={{ fontSize: 'clamp(11px, 3vw, 13px)', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>{item.v}</p>
            </div>
          ))}
        </div>

        {/* ===== MONTHLY MYSTERY REPORTS ===== */}
        <MysteryReportsSection />

        {/* Domains */}
        <div style={{ padding: '16px' }}>
          
          {/* DOMAIN 1: HEALTH - Causal Structure */}
          <DomainSection num={1} title="Health & Longevity" onInView={setActiveDomain} introOpen={introOpen} aiContent={aiContent} domainKey="health" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Algorithm-driven constitution from Master Health Engine */}
            <Section title="Constitution">
              <Text>{edHealth.constitution?.content || edHealth?.constitution?.content || d1.constitution?.type || d1.constitution_cause}</Text>
            </Section>
            
            {/* ENHANCED: Health Tripod (Deha-Manas-Prana) from Master Health Engine */}
            {edHealth.tripod && (
              <Section title="Health Vitality Index">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Text>{edHealth.tripod?.content}</Text>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                    <div style={{ padding: '10px 14px', background: 'rgba(46,204,113,0.1)', borderRadius: '10px', flex: 1, minWidth: '80px', textAlign: 'center' }}>
                      <div style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', fontWeight: '600', color: 'rgba(46,204,113,0.9)' }}>{(edHealth.tripod?.deha || 0) >= 0.7 ? 'Strong' : (edHealth.tripod?.deha || 0) >= 0.4 ? 'Moderate' : 'Developing'}</div>
                      <div style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Body</div>
                    </div>
                    <div style={{ padding: '10px 14px', background: 'rgba(116,179,255,0.1)', borderRadius: '10px', flex: 1, minWidth: '80px', textAlign: 'center' }}>
                      <div style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', fontWeight: '600', color: 'rgba(116,179,255,0.9)' }}>{(edHealth.tripod?.manas || 0) >= 0.7 ? 'Strong' : (edHealth.tripod?.manas || 0) >= 0.4 ? 'Moderate' : 'Developing'}</div>
                      <div style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Mind</div>
                    </div>
                    <div style={{ padding: '10px 14px', background: 'rgba(255,200,100,0.1)', borderRadius: '10px', flex: 1, minWidth: '80px', textAlign: 'center' }}>
                      <div style={{ fontSize: 'clamp(13px, 3.5vw, 15px)', fontWeight: '600', color: 'rgba(255,200,100,0.9)' }}>{(edHealth.tripod?.prana || 0) >= 0.7 ? 'Strong' : (edHealth.tripod?.prana || 0) >= 0.4 ? 'Moderate' : 'Developing'}</div>
                      <div style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>Vitality</div>
                    </div>
                  </div>
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Longevity from Master Health Engine */}
            {edHealth?.longevity?.range && (
              <Section title="Life Expectancy">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text><strong>{edHealth.longevity?.class?.replace('_', ' ')}</strong> — {edHealth.longevity?.range}</Text>
                  {edHealth.longevity?.lifespan_estimate && (
                    <Text style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.6)' }}>Estimated lifespan: {edHealth.longevity?.lifespan_estimate}</Text>
                  )}
                </div>
              </Section>
            )}
            <Section title="Effect">
              <Text>{d1.constitution?.practical_meaning || d1.constitution_effect}</Text>
            </Section>
            <Section title="Behavioral Triggers">
              <Text>{d1.constitution?.behavioral_triggers || d1.behavioral_triggers}</Text>
            </Section>
            {d1.vulnerable_areas?.length > 0 && (
              <Section title="Areas Requiring Attention">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {d1.vulnerable_areas.map((v, i) => (
                    <span key={i} style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', padding: '8px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '16px', color: 'rgba(255,255,255,0.75)' }}>
                      {v.area}
                    </span>
                  ))}
                </div>
              </Section>
            )}
            <Section title="Mental & Emotional Structure">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {typeof d1.mental_wellbeing === 'object' && d1.mental_wellbeing?.emotional_nature ? (
                  <>
                    <Text><strong>Emotional Nature:</strong> {d1.mental_wellbeing.emotional_nature}</Text>
                    <Text><strong>Emotional Needs:</strong> {d1.mental_wellbeing.emotional_needs}</Text>
                    <Text><strong>Stress Response:</strong> {d1.mental_wellbeing.stress_response}</Text>
                    <Text><strong>Growth Path:</strong> {d1.mental_wellbeing.emotional_growth}</Text>
                  </>
                ) : typeof d1.mental_wellbeing === 'object' ? (
                  <>
                    <Text>{d1.mental_wellbeing?.baseline}</Text>
                    <Text>{d1.mental_wellbeing?.stability}</Text>
                    <Text>{d1.mental_wellbeing?.management}</Text>
                  </>
                ) : typeof d1.mental_health === 'object' ? (
                  <>
                    <Text>{d1.mental_health?.attention}</Text>
                    <Text>{d1.mental_health?.cause}</Text>
                    <Text>{d1.mental_health?.control}</Text>
                  </>
                ) : <Text>{d1.mental_health}</Text>}
              </div>
            </Section>
            <Section title="Life Expectancy">
              {typeof d1.longevity === 'object' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>
                    <strong>
                      {d1.longevity?.indication?.replace('Purna Ayuh', 'Long Life')?.replace('Madhya Ayuh', 'Moderate')?.replace('Alpa Ayuh', 'Requires Attention') || 'Long Life'}
                    </strong> — {d1.longevity?.range}
                  </Text>
                  <Text>{d1.longevity?.note || d1.longevity?.interpretation}</Text>
                  {d1.longevity?.lifespan_estimate && (
                    <Text style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.6)' }}>
                      Estimated lifespan: {d1.longevity.lifespan_estimate}
                    </Text>
                  )}
                  {/* Protection & Risk Analysis */}
                  {(d1.longevity?.protection_factors > 0 || d1.longevity?.maraka_score > 0) && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '10px', 
                      background: 'rgba(255,255,255,0.03)', 
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.08)'
                    }}>
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        {d1.longevity?.protection_factors > 0 && (
                          <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(74, 222, 128, 0.9)' }}>
                            Protection: {d1.longevity.protection_factors >= 70 ? 'Strong' : d1.longevity.protection_factors >= 40 ? 'Moderate' : 'Developing'}
                          </span>
                        )}
                        {d1.longevity?.maraka_score > 30 && (
                          <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(251, 191, 36, 0.9)' }}>
                            Risk Level: {d1.longevity.maraka_score < 50 ? 'Low' : d1.longevity.maraka_score < 70 ? 'Moderate' : 'High'}
                          </span>
                        )}
                      </div>
                      {d1.longevity?.death_timing?.most_likely_dasha && (
                        <Text style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', marginTop: '6px' }}>
                          Critical period: {d1.longevity.death_timing.most_likely_dasha} Mahadasha
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              ) : <Text>{d1.longevity}</Text>}
            </Section>
            <Section title="Current Phase">
              <Text>{d1.health_transitions?.current_phase || d1.current_phase?.description || d1.current_phase}</Text>
            </Section>
            
            {/* ENHANCED: Age Gate, Seasonal, and Enhanced Events - Always render for AI content */}
            {(d1.enhanced_predictions?.available || aiContent?.life_insights?.health) && (
              <>
                <Section title="Developmental Phase">
                  {d1.enhanced_predictions?.age_gate ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <span style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: '500', color: 'rgba(255,255,255,0.9)' }}>
                        {d1.enhanced_predictions.age_gate?.phase}
                      </span>
                      <span style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.5)', marginLeft: '8px' }}>
                        (Age {d1.enhanced_predictions.age_gate?.current_age})
                      </span>
                    </div>
                    <Text><strong>Focus:</strong> {d1.enhanced_predictions.age_gate?.focus}</Text>
                    <Text><strong>Themes:</strong> {d1.enhanced_predictions.age_gate?.themes?.join(', ')}</Text>
                    <Text><strong>Sensitive Areas:</strong> {d1.enhanced_predictions.age_gate?.sensitive_areas?.join(', ')}</Text>
                  </div>
                  ) : null}
                </Section>
                
                <Section title="Seasonal Health">
                  {d1.enhanced_predictions?.seasonal ? (
                  <div style={{ 
                    padding: '12px', 
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.08)'
                  }}>
                    <Text>{d1.enhanced_predictions.seasonal?.advice || "Pay attention to seasonal changes and maintain consistent routines."}</Text>
                    {d1.enhanced_predictions.seasonal?.themes?.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.6)' }}>
                        Focus areas: {d1.enhanced_predictions.seasonal?.themes?.join(', ')}
                      </div>
                    )}
                  </div>
                  ) : null}
                </Section>
                
                {d1.enhanced_predictions?.enhanced_events?.length > 0 && (
                  <Section title="Current Alerts">
                    {d1.enhanced_predictions.enhanced_events.map((event, i) => (
                      <div key={i} style={{ 
                        padding: '10px 12px', 
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        border: '1px solid rgba(255,255,255,0.08)'
                      }}>
                        <div style={{ fontSize: 'clamp(11px, 3vw, 13px)', fontWeight: '500', color: 'rgba(255,255,255,0.9)' }}>{event.title}</div>
                        <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.6)', marginTop: '4px' }}>{event.description}</div>
                      </div>
                    ))}
                  </Section>
                )}
                
                {(d1.enhanced_predictions?.past_phases?.length > 0 || aiContent?.life_insights?.health) && (
                  <Section title="Past Health Phases">
                    {d1.enhanced_predictions?.past_phases?.map((phase, i) => (
                      <div key={i} style={{ 
                        padding: '10px 12px', 
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        borderLeft: '2px solid rgba(255,255,255,0.15)'
                      }}>
                        <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>{phase.phase}</div>
                        <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{phase.dasha_theme?.replace('dasha', 'period')?.replace('Dasha', 'Period')}</div>
                        <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>{phase.description}</div>
                      </div>
                    ))}
                  </Section>
                )}
                
                {/* Gender-Specific Health Profile - Use healthTimelineData (accurate) or fall back to d1.enhanced_predictions */}
                {(healthTimelineData?.male_health_profile?.available || healthTimelineData?.female_health_profile?.available || d1.enhanced_predictions?.male_health_profile?.available || d1.enhanced_predictions?.female_health_profile?.available || aiContent?.life_insights?.health) && (
                  <Section title="Constitution Profile">
                    {(() => {
                      // Prefer healthTimelineData (from /api/health-timeline/complete) as it has accurate chart-based detection
                      const htProfile = healthTimelineData?.male_health_profile || healthTimelineData?.female_health_profile;
                      const epProfile = d1.enhanced_predictions?.male_health_profile || d1.enhanced_predictions?.female_health_profile;
                      const profile = htProfile?.available ? htProfile : epProfile;
                      const isMale = !!(healthTimelineData?.male_health_profile || d1.enhanced_predictions?.male_health_profile);
                      
                      if (!profile?.available) return null;
                      
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {/* Constitution Type */}
                          <div style={{ 
                            padding: '14px', 
                            background: 'rgba(255,255,255,0.03)',
                            borderRadius: '10px',
                            borderLeft: '2px solid rgba(255,255,255,0.15)'
                          }}>
                            <div style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                              {profile.constitution_type === 'Standard' ? 'Balanced Constitution' : profile.constitution_type || 'Balanced Constitution'}
                            </div>
                            {profile.core_theme && (
                              <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.6)', marginTop: '6px' }}>{profile.core_theme}</div>
                            )}
                          </div>
                          
                          {/* Mental Health Pattern */}
                          {profile.mental_health && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: '500', color: 'rgba(255,255,255,0.8)', marginBottom: '6px' }}>
                                {profile.mental_health.type || 'Mental Pattern'}
                              </div>
                              <Text>{profile.mental_health.description}</Text>
                              {profile.mental_health.management && (
                                <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', marginTop: '6px' }}>
                                  {profile.mental_health.management}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Vulnerabilities (for male) */}
                          {isMale && profile.vulnerabilities?.length > 0 && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '8px', fontWeight: '500' }}>ATTENTION AREAS</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {profile.vulnerabilities.slice(0, 5).map((v, i) => (
                                  <span key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(116,179,255,0.06)', borderRadius: '12px', color: 'rgba(255,255,255,0.7)' }}>
                                    {v}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Lagna Vulnerability (for male with Cancer Lagna) */}
                          {isMale && profile.lagna_vulnerability && (
                            <div style={{ padding: '12px', background: 'rgba(116,179,255,0.04)', borderRadius: '8px', border: '1px solid rgba(116,179,255,0.15)' }}>
                              <Text>{profile.lagna_vulnerability.description}</Text>
                              {profile.lagna_vulnerability.body_focus?.length > 0 && (
                                <div style={{ marginTop: '8px', fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)' }}>
                                  Focus areas: {profile.lagna_vulnerability.body_focus.join(', ')}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Critical Warnings */}
                          {profile.critical_warnings?.length > 0 && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: '500', color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Critical Period</div>
                              {profile.critical_warnings.map((w, i) => (
                                <div key={i}>
                                  <Text>{w.description}</Text>
                                  <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>{w.warning}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Hormonal Status (for female) */}
                          {!isMale && profile.hormonal_status && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: '500', color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>Hormonal Balance</div>
                              <Text>{profile.hormonal_status.description || 'Regular attention to hormonal health supports overall wellness.'}</Text>
                              {profile.hormonal_status.management && (
                                <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', marginTop: '6px' }}>{profile.hormonal_status.management}</div>
                              )}
                            </div>
                          )}
                          
                          {/* Recommendations */}
                          {profile.recommendations?.length > 0 && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '8px', fontWeight: '500' }}>RECOMMENDATIONS</div>
                              {profile.recommendations.map((r, i) => (
                                <div key={i} style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>• {r}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </Section>
                )}
                
                {/* Health Timeline - Prefer healthTimelineData for accuracy */}
                {(healthTimelineData?.health_timeline?.length > 0 || d1.enhanced_predictions?.health_timeline?.length > 0 || aiContent?.life_insights?.health) && (
                  <Section title="Life Health Timeline">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(healthTimelineData?.health_timeline || d1.enhanced_predictions?.health_timeline || []).map((phase, i) => {
                        const isPast = phase.status === 'past';
                        const isCurrent = phase.status === 'current';
                        return (
                          <div key={i} style={{
                            padding: '12px',
                            background: isCurrent ? 'rgba(116,179,255,0.06)' : 'rgba(255,255,255,0.02)',
                            borderRadius: '8px',
                            border: isCurrent ? '1px solid rgba(116,179,255,0.2)' : '1px solid rgba(255,255,255,0.05)',
                            opacity: isPast ? 0.7 : 1
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ 
                                  fontSize: 'clamp(10px, 2.8vw, 12px)', 
                                  fontWeight: '500',
                                  color: isCurrent ? '#74B3FF' : 'rgba(255,255,255,0.7)'
                                }}>
                                  {phase.phase}
                                </span>
                                {isCurrent && (
                                  <span style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', padding: '2px 8px', background: 'rgba(116,179,255,0.15)', borderRadius: '10px', color: '#74B3FF' }}>NOW</span>
                                )}
                              </div>
                              <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.4)' }}>{phase.age_range}</span>
                            </div>
                            
                            {phase.health_patterns?.length > 0 && (
                              <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.6)' }}>
                                {phase.health_patterns.slice(0, 3).join(' • ')}
                              </div>
                            )}
                            
                            {phase.critical_note && (
                              <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.6)' }}>
                                {phase.critical_note}
                              </div>
                            )}
                            
                            {phase.longevity && (
                              <div style={{ marginTop: '6px', fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)' }}>
                                {phase.longevity}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Section>
                )}
                
                {/* Universal Health Profile - Comprehensive patterns from chart */}
                {(healthTimelineData?.universal_health_profile?.available || aiContent?.life_insights?.health) && (
                  <Section title="Comprehensive Health Profile">
                    {(() => {
                      const uhp = healthTimelineData?.universal_health_profile;
                      if (!uhp) return null;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {/* Rising Sign Constitution */}
                          {uhp.lagna_based && (
                            <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
                              <div style={{ fontSize: 'clamp(11px, 3vw, 13px)', fontWeight: '600', color: 'rgba(255,255,255,0.9)', marginBottom: '8px' }}>
                                {uhp.lagna_based.sign} Body Type ({uhp.lagna_based.element})
                              </div>
                              <Text>{uhp.lagna_based.constitution}</Text>
                              <div style={{ marginTop: '8px', fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)' }}>
                                {uhp.lagna_based.stress_pattern}
                              </div>
                              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {uhp.lagna_based.body_theme?.map((t, i) => (
                                  <span key={i} style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', padding: '3px 8px', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', color: 'rgba(255,255,255,0.6)' }}>{t}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Health Challenge House */}
                          {uhp.house_patterns?.sixth_house && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: '600', color: 'rgba(255,255,255,0.8)', marginBottom: '6px' }}>
                                Health Challenges Active ({uhp.house_patterns.sixth_house.planets?.join(' + ')})
                              </div>
                              {uhp.house_patterns.sixth_house.patterns?.slice(0, 2).map((p, i) => (
                                <div key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>• {p}</div>
                              ))}
                              {uhp.house_patterns.sixth_house.warning && (
                                <div style={{ marginTop: '8px', fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>
                                  {uhp.house_patterns.sixth_house.warning}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Moon Pattern */}
                          {uhp.moon_pattern && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: '500', color: 'rgba(255,255,255,0.8)', marginBottom: '6px' }}>
                                Moon in {uhp.moon_pattern.sign}
                              </div>
                              <Text>{uhp.moon_pattern.mental_pattern}</Text>
                              <div style={{ marginTop: '6px', fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)' }}>
                                Stress response: {uhp.moon_pattern.stress_response}
                              </div>
                            </div>
                          )}
                          
                          {/* Planet Clusters */}
                          {uhp.planet_clusters?.length > 0 && uhp.planet_clusters.map((c, i) => (
                            <div key={i} style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: '500', color: 'rgba(255,255,255,0.8)', marginBottom: '6px' }}>
                                {c.sign} Cluster ({c.planets?.join(', ')})
                              </div>
                              <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)' }}>
                                Body areas: {c.body_areas?.join(', ')}
                              </div>
                              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {c.vulnerabilities?.map((v, j) => (
                                  <span key={j} style={{ fontSize: 'clamp(9px, 2.2vw, 10px)', padding: '3px 8px', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', color: 'rgba(255,255,255,0.6)' }}>{v}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                          
                          {/* Vulnerabilities Summary */}
                          {uhp.vulnerabilities?.length > 0 && (
                            <div style={{ padding: '12px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', borderLeft: '2px solid rgba(255,255,255,0.15)' }}>
                              <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', marginBottom: '8px', fontWeight: '500' }}>KEY VULNERABILITIES</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {uhp.vulnerabilities.slice(0, 6).map((v, i) => (
                                  <span key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(255,255,255,0.06)', borderRadius: '12px', color: 'rgba(255,255,255,0.6)' }}>{v}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </Section>
                )}
              </>
            )}
            
          </DomainSection>

          {/* DOMAIN 2: CAREER - With What Fails */}
          <DomainSection num={2} title="Career & Profession" onInView={setActiveDomain} introOpen={introOpen} aiContent={aiContent} domainKey="career" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Career Archetype from Master Career Algorithm */}
            {edCareer?.archetype?.content && (
              <Section title="Work Orientation">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Text>{edCareer?.archetype?.content}</Text>
                  {edCareer?.archetype?.career_tier && (
                    <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(116,179,255,0.1)', borderRadius: '12px', color: 'rgba(116,179,255,0.9)', alignSelf: 'flex-start' }}>{edCareer?.archetype?.career_tier}</span>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Best Fields from Master Career Algorithm */}
            {edCareer?.best_fields?.items?.length > 0 && (
              <Section title="Best Career Fields">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {edCareer.best_fields.items.map((field, i) => (
                    <span key={i} style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', padding: '8px 14px', background: 'rgba(46,204,113,0.08)', borderRadius: '12px', color: 'rgba(46,204,113,0.9)' }}>{field}</span>
                  ))}
                </div>
                {edCareer.best_fields.career_model && (
                  <Text style={{ marginTop: '10px', color: 'rgba(255,255,255,0.7)' }}>{edCareer.best_fields.career_model}</Text>
                )}
              </Section>
            )}
            
            {/* ENHANCED: Career Challenges from Master Career Algorithm */}
            {edCareer?.challenges?.items?.length > 0 && (
              <Section title="Career Challenges">
                <div style={{ padding: '12px', background: 'rgba(255,100,100,0.05)', borderRadius: '10px', borderLeft: '2px solid rgba(255,100,100,0.3)' }}>
                  {edCareer.challenges.items.map((challenge, i) => (
                    <Text key={i} style={{ color: 'rgba(255,200,200,0.8)', marginBottom: '6px' }}>{challenge}</Text>
                  ))}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Entrepreneurial Score from Master Career Algorithm */}
            {edCareer?.entrepreneurial?.score > 0 && (
              <Section title="Entrepreneurial Potential">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ padding: '10px 16px', background: edCareer?.entrepreneurial?.suitable ? 'rgba(46,204,113,0.1)' : 'rgba(255,200,100,0.1)', borderRadius: '10px' }}>
                    <div style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: '600', color: edCareer?.entrepreneurial?.suitable ? 'rgba(46,204,113,0.9)' : 'rgba(255,200,100,0.9)' }}>
                      {edCareer?.entrepreneurial?.suitable ? 'Strong' : 'Moderate'}
                    </div>
                  </div>
                  <Text>{edCareer?.entrepreneurial?.suitable ? 'Entrepreneurship is strongly indicated in your chart. Independent ventures can bring great success.' : 'Employment path may be more stable for you, though side ventures can complement income.'}</Text>
                </div>
              </Section>
            )}
            
            {/* Fallback: Original Work Orientation */}
            {!edCareer?.archetype?.content && d2.work_orientation && (
              <Section title="Work Orientation">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Text>{d2.work_orientation?.summary}</Text>
                  {d2.work_orientation?.patterns?.length > 0 && (
                    <div style={{ marginTop: '4px' }}>
                      <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', fontWeight: '500', marginBottom: '6px' }}>CAREER PATTERNS</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {d2.work_orientation.patterns.map((p, i) => (
                          <span key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(116,179,255,0.08)', borderRadius: '12px', color: 'rgba(255,255,255,0.75)' }}>{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: '4px', padding: '10px 12px', background: 'rgba(46,204,113,0.05)', borderRadius: '8px' }}>
                    <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(46,204,113,0.7)', fontWeight: '500', marginBottom: '4px' }}>PERFORMS BEST</div>
                    <Text style={{ color: 'rgba(255,255,255,0.8)' }}>{d2.work_orientation?.performs_best}</Text>
                  </div>
                  <Text style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.6)' }}>{d2.work_orientation?.work_style}</Text>
                </div>
              </Section>
            )}
            
            <Section title="Career Model">
              {typeof d2.career_model === 'object' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text>{d2.career_model?.archetype}</Text>
                  <Text>{d2.career_model?.core_drive}</Text>
                  <Text>{d2.career_model?.evolution}</Text>
                </div>
              ) : typeof d2.authority_model === 'object' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text>{d2.authority_model?.type}</Text>
                  <Text>{d2.authority_model?.activation}</Text>
                  <Text>{d2.authority_model?.peak_years}</Text>
                </div>
              ) : <Text>{d2.career_structure}</Text>}
            </Section>
            <Section title="What Works">
              {d2.what_works ? (
                <>
                  <Tags items={d2.what_works?.industries || d2.what_works?.best_industries || d2.best_industries} />
                  <div style={{ marginTop: '8px' }}><Text>{d2.what_works?.integration_note}</Text></div>
                  <div style={{ marginTop: '8px' }}><Tags items={d2.what_works?.strengths || d2.what_works?.yogas_supporting} /></div>
                  {d2.what_works?.success_when?.length > 0 && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: 'rgba(46,204,113,0.05)', borderRadius: '8px' }}>
                      <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(46,204,113,0.7)', fontWeight: '500', marginBottom: '6px' }}>SUCCESS WHEN</div>
                      {d2.what_works.success_when.map((s, i) => (
                        <div key={i} style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.7)', marginBottom: '4px', paddingLeft: '8px', borderLeft: '2px solid rgba(46,204,113,0.3)' }}>{s}</div>
                      ))}
                    </div>
                  )}
                </>
              ) : <Tags items={d2.best_industries} />}
            </Section>
            {d2.what_fails && (
              <Section title="What Fails">
                <div style={{ padding: '14px', background: 'rgba(255,100,100,0.05)', borderRadius: '10px', borderLeft: '2px solid rgba(255,100,100,0.3)' }}>
                  <Text style={{ color: 'rgba(255,200,200,0.8)' }}>{d2.what_fails?.warning || d2.what_fails?.friction_causes}</Text>
                  <Text style={{ marginTop: '8px', color: 'rgba(255,150,150,0.7)' }}>Avoid: {d2.what_fails?.avoid}</Text>
                </div>
              </Section>
            )}
            
            {/* Entrepreneurial Potential */}
            {d2.entrepreneurial_potential && (
              <Section title="Entrepreneurial Potential">
                <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Text style={{ fontWeight: '500' }}>{d2.entrepreneurial_potential?.indication}</Text>
                  <Text style={{ marginTop: '6px', color: 'rgba(255,255,255,0.6)' }}>{d2.entrepreneurial_potential?.pattern}</Text>
                </div>
              </Section>
            )}
            
            {/* Career Profile - Simplified */}
            {(healthTimelineData?.career_profile_v2?.available || aiContent?.life_insights?.career) && (
              <Section title="Career Path Analysis">
                {(() => {
                  const cp = healthTimelineData?.career_profile_v2;
                  if (!cp) return null;
                  const struct = cp.career_structure || {};
                  const state = cp.current_state || {};
                  const de = cp.dasha_career_effects || {};
                  
                  // Determine career path description
                  const pathDesc = state.state === 'FOUNDER' ? 
                    'Your chart strongly indicates an entrepreneurial path. Business ownership and independent ventures lead to greatest success.' :
                    state.state === 'HYBRID' ?
                    'You are suited for multiple income streams - combining employment with side ventures works well for you.' :
                    state.state === 'TRANSITION' ?
                    'You are in a career transition phase. This is a period of pivoting and exploring new directions.' :
                    'A structured career path with steady growth suits you well.';
                  
                  const tendencyDesc = struct.primary_tendency === 'Business' ?
                    'Business and entrepreneurship are your natural strengths. Independent ventures outperform traditional employment for you.' :
                    struct.primary_tendency === 'Service' ?
                    'Professional employment offers stable growth. Corporate or organizational roles suit your skills.' :
                    'You have balanced abilities for both employment and business ventures.';
                  
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {/* Career Path */}
                      <Text>{pathDesc}</Text>
                      
                      {/* Career Tendency */}
                      <Text>{tendencyDesc}</Text>
                      
                      {/* Current Phase */}
                      {de.career_effect && (
                        <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                          <div style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', fontWeight: '500', color: 'rgba(255,255,255,0.9)', marginBottom: '6px' }}>
                            Current Phase Impact
                          </div>
                          <Text>{de.career_effect}</Text>
                        </div>
                      )}
                      
                      {/* Recommendations */}
                      {cp.recommendations?.length > 0 && (
                        <div style={{ padding: '12px', background: 'rgba(46,204,113,0.05)', borderRadius: '8px' }}>
                          <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(46,204,113,0.7)', marginBottom: '8px', fontWeight: '500' }}>RECOMMENDATIONS</div>
                          {cp.recommendations.map((r, i) => (
                            <div key={i} style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>• {r}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </Section>
            )}
            
            <Section title="Career Timeline"><Phases data={d2.career_phases || d2.career_timing} /></Section>
          </DomainSection>

          {/* DOMAIN 3: WEALTH - Simplified User-Friendly */}
          <DomainSection num={3} title="Wealth & Finances" onInView={setActiveDomain} introOpen={introOpen} aiContent={aiContent} domainKey="wealth" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Wealth Potential from Master Wealth Engine */}
            {edWealth?.potential && (
              <Section title="Financial Potential">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ padding: '12px 16px', background: 'rgba(46,204,113,0.1)', borderRadius: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Wealth Tier</div>
                    <div style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: '600', color: 'rgba(46,204,113,0.9)' }}>{edWealth?.potential?.tier}</div>
                  </div>
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Wealth Sources from Master Wealth Engine */}
            {edWealth?.sources?.primary && (
              <Section title="Wealth Sources">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text><strong>Primary Source:</strong> {edWealth?.sources?.primary}</Text>
                  {edWealth?.sources?.source_mix && Object.keys(edWealth?.sources?.source_mix).length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {Object.entries(edWealth?.sources?.source_mix).map(([source, value], i) => (
                        <span key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '6px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', color: 'rgba(255,255,255,0.7)' }}>{source}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Business Potential from Master Wealth Engine */}
            {edWealth?.business && (
              <Section title="Business Potential">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ padding: '10px 16px', background: edWealth.business.suitable ? 'rgba(46,204,113,0.1)' : 'rgba(255,200,100,0.1)', borderRadius: '10px' }}>
                    <div style={{ fontSize: 'clamp(12px, 3.2vw, 14px)', fontWeight: '600', color: edWealth.business.suitable ? 'rgba(46,204,113,0.9)' : 'rgba(255,200,100,0.9)' }}>
                      {edWealth.business.suitable ? 'Highly Favorable' : 'Moderate'}
                    </div>
                  </div>
                  <Text>{edWealth.business.suitable ? 'Business ownership is strongly indicated in your chart. Independent ventures can lead to significant wealth.' : 'Employment may provide more stable income growth. Consider supplementing with small ventures.'}</Text>
                </div>
              </Section>
            )}
            
            {/* Fallback: Original Financial Potential */}
            {!edWealth?.potential && d3.wealth_potential && (
              <Section title="Financial Potential">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>{d3.wealth_potential?.nature || d3.wealth_potential?.cause || "Your chart indicates capacity for wealth building through disciplined effort."}</Text>
                  <Text>{d3.wealth_potential?.path || "Structured, knowledge-driven ventures favor wealth building."}</Text>
                </div>
              </Section>
            )}
            
            {/* Universal Wealth Profile - Simplified */}
            {unifiedStoryData?.universal_wealth_profile?.available && (() => {
              const uwp = unifiedStoryData.universal_wealth_profile;
              const wealthPath = uwp.wealth_sources?.primary === 'business' ? 
                'Business ownership and entrepreneurial ventures offer the strongest wealth path for you.' :
                uwp.wealth_sources?.primary === 'salary' ?
                'Professional employment with career progression offers stable wealth building.' :
                'A combination of employment income and business ventures suits your wealth profile.';
              
              const ceilingDesc = uwp.ceiling?.level === 'billionaire' ? 
                'Very high wealth potential indicated. Success depends on sustained effort, right timing, and wise decisions.' :
                uwp.ceiling?.level === 'multimillionaire' ?
                'Strong wealth potential indicated. Consistent effort leads to significant accumulation.' :
                'Steady wealth building indicated through disciplined financial management.';
              
              return (
                <>
                  {/* Wealth Path - Narrative */}
                  <Section title="Your Wealth Path">
                    <Text>{wealthPath}</Text>
                    {uwp.wealth_sources?.secondary && (
                      <Text style={{ marginTop: '8px', color: 'rgba(255,255,255,0.6)' }}>
                        Secondary income through: {uwp.wealth_sources.secondary}
                      </Text>
                    )}
                  </Section>
                  
                  {/* Wealth Role - Simplified */}
                  <Section title="How You Build Wealth">
                    <Text>
                      {uwp.wealth_role?.role === 'HYBRID' || uwp.wealth_role?.description?.includes('Hybrid') ?
                        'You have the ability to build wealth through both business ownership and professional expertise. Combining these approaches maximizes your earning potential.' :
                        uwp.wealth_role?.role === 'OWNER' ?
                        'Your wealth building strength lies in ownership and entrepreneurship. Building and owning assets creates your best financial outcomes.' :
                        uwp.wealth_role?.role === 'EXECUTOR' ?
                        'Your wealth building strength lies in professional expertise and skilled execution. Career advancement and professional excellence drive your income growth.' :
                        uwp.wealth_role?.description || "You have the ability to build wealth through both ownership and skilled execution."
                      }
                    </Text>
                  </Section>
                  
                  {/* Wealth Potential - No confusing scores */}
                  <Section title="Wealth Potential">
                    <Text>{ceilingDesc}</Text>
                  </Section>
                  
                  {/* Financial Habits - Simplified */}
                  <Section title="Financial Habits">
                    <Text>
                      {uwp.retention?.level === 'STRONG' ? 
                        'You have good wealth retention ability. Money tends to stay and accumulate with you.' :
                        uwp.retention?.level === 'MODERATE' ?
                        'Moderate wealth retention. With discipline, savings improve over time.' :
                        'Building savings discipline will help wealth accumulation.'}
                    </Text>
                  </Section>
                  
                  {/* Current Financial Phase - Simplified */}
                  <Section title="Current Financial Phase">
                    <Text>{uwp.current_state?.description || "Active wealth building phase. Focus on consistent growth."}</Text>
                  </Section>
                </>
              );
            })()}
            
            <Section title="Income Sources">
              <Tags items={d3.income_sources?.favorable || d3.primary_income_sources} />
              {(d3.income_sources?.mode || d3.income_sources?.logic) && <Text style={{ marginTop: '8px' }}>{d3.income_sources?.mode || d3.income_sources?.logic}</Text>}
            </Section>
            {d3.wealth_risks && (
              <Section title="Financial Cautions">
                <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', borderLeft: '2px solid rgba(255,159,67,0.4)' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.8)' }}>{d3.wealth_risks?.avoid_speculation || "Avoid speculative investments without proper research."}</Text>
                  <Text style={{ marginTop: '6px', color: 'rgba(255,255,255,0.7)' }}>{d3.wealth_risks?.avoid_informal || d3.wealth_risks?.avoid_partnerships || "Ensure clear documentation for all financial partnerships."}</Text>
                </div>
              </Section>
            )}
            <Section title="Financial Timeline"><Phases data={d3.wealth_phases} /></Section>
          </DomainSection>

          {/* DOMAIN 4: EDUCATION - Knowledge as Authority */}
          <DomainSection num={4} title="Education & Intellect" onInView={setActiveDomain} introOpen={introOpen} aiContent={aiContent} domainKey="education" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Learning Style from Master Education Engine */}
            {edEducation?.learning?.style && (
              <Section title="Learning Style">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text>{edEducation.learning.style}</Text>
                  {edEducation.learning.intelligence_type && (
                    <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(147,51,234,0.1)', borderRadius: '12px', color: 'rgba(147,51,234,0.9)', alignSelf: 'flex-start' }}>{edEducation.learning.intelligence_type}</span>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Best Subjects from Master Education Engine */}
            {edEducation?.subjects?.items?.length > 0 && (
              <Section title="Best Subjects">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {edEducation?.subjects?.items.map((subject, i) => (
                    <span key={i} style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', padding: '8px 14px', background: 'rgba(147,51,234,0.08)', borderRadius: '12px', color: 'rgba(147,51,234,0.9)' }}>{subject}</span>
                  ))}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Higher Education from Master Education Engine */}
            {edEducation?.higher_education && (
              <Section title="Higher Education">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {edEducation.higher_education.prospects && (
                    <Text>{edEducation.higher_education.prospects}</Text>
                  )}
                  {edEducation.higher_education.foreign_education && (
                    <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '6px 12px', background: 'rgba(46,204,113,0.1)', borderRadius: '12px', color: 'rgba(46,204,113,0.9)', alignSelf: 'flex-start' }}>Foreign Education Indicated</span>
                  )}
                </div>
              </Section>
            )}
            
            {/* Fallback to original sections */}
            {!edEducation?.learning?.style && (
              <>
                <Section title="Knowledge as Authority">
                  <Text>{d4.knowledge_value || d4.knowledge_as_authority}</Text>
                </Section>
                <Section title="Learning Style">
                  {typeof d4.learning_profile === 'object' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <Text>{d4.learning_profile?.style}</Text>
                      <Text>{d4.learning_profile?.strength}</Text>
                      <Text>{d4.learning_profile?.application}</Text>
                    </div>
                  ) : typeof d4.learning_style === 'object' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <Text>{d4.learning_style?.type}</Text>
                      <Text>{d4.learning_style?.why}</Text>
                      <Text>{d4.learning_style?.monetization}</Text>
                    </div>
                  ) : <Text>{d4.learning_style}</Text>}
                </Section>
                <Section title="Best Subjects">
                  <Tags items={d4.best_domains || d4.best_subjects} />
                </Section>
              </>
            )}
            <Section title="Credentials Impact">
              <Text>{d4.credentials_impact}</Text>
            </Section>
          </DomainSection>

          {/* DOMAIN 5: MARRIAGE - Conditional Timing */}
          <DomainSection num={5} title="Relationships & Marriage" onInView={setActiveDomain} introOpen={introOpen} aiContent={aiContent} domainKey="marriage" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Marriage Timing from Master Marriage Engine */}
            {edMarriage?.timing?.best_age && (
              <Section title="Marriage Timing">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text><strong>Best Age:</strong> {edMarriage?.timing?.best_age}</Text>
                  {edMarriage?.timing?.marriage_tier && (
                    <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(236,72,153,0.1)', borderRadius: '12px', color: 'rgba(236,72,153,0.9)', alignSelf: 'flex-start' }}>{edMarriage?.timing?.marriage_tier}</span>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Spouse Profile from Master Marriage Engine */}
            {edMarriage?.spouse && (
              <Section title="Partner Profile">
                <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                  {edMarriage?.spouse?.partner_type && (
                    <Text style={{ marginBottom: '8px' }}>{edMarriage?.spouse?.partner_type}</Text>
                  )}
                  {edMarriage?.spouse?.qualities?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                      {edMarriage?.spouse?.qualities.map((q, i) => (
                        <span key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(236,72,153,0.08)', borderRadius: '12px', color: 'rgba(255,255,255,0.7)' }}>{q}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Marital Harmony from Master Marriage Engine */}
            {edMarriage?.harmony?.level && (
              <Section title="Marital Harmony">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text><strong>Harmony Level:</strong> {edMarriage?.harmony?.level}</Text>
                  {edMarriage?.harmony?.marriage_type && (
                    <Text>Marriage Type: {edMarriage?.harmony?.marriage_type}</Text>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Relationship Dynamics from marriage_relationship engine */}
            {edMarriage?.dynamics && (
              <Section title="Relationship Dynamics">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {edMarriage.dynamics.promise_strength && (
                    <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                      <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>Promise Strength</div>
                      <Text>{edMarriage.dynamics.promise_strength}</Text>
                    </div>
                  )}
                  {edMarriage.dynamics.harmony_description && (
                    <Text>{edMarriage.dynamics.harmony_description}</Text>
                  )}
                  {edMarriage.dynamics.type_description && (
                    <Text style={{ color: 'rgba(255,255,255,0.7)' }}>{edMarriage.dynamics.type_description}</Text>
                  )}
                </div>
              </Section>
            )}
            
            {/* Fallback: Original timing */}
            {!edMarriage?.timing?.best_age && (d5.timing || d5.timing_conditional) && (
              <Section title="Marriage Timing">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text>{d5.timing?.indication || d5.timing_conditional?.maturity_condition}</Text>
                  <Text>{d5.timing?.maturity_note || d5.timing_conditional?.stability_window}</Text>
                  <Text>{d5.timing?.condition}</Text>
                </div>
              </Section>
            )}
            
            {/* Universal Marriage Profile from unified_story_data */}
            {unifiedStoryData?.universal_marriage_profile?.available && (() => {
              const ump = unifiedStoryData.universal_marriage_profile;
              
              // Convert technical data to user-friendly descriptions
              const marriageStrength = ump.marriage_promise?.strength === 'strong' || ump.marriage_promise?.score >= 60 ?
                'Marriage is strongly indicated in your chart. A committed relationship is a natural part of your life path.' :
                ump.marriage_promise?.strength === 'moderate' || ump.marriage_promise?.score >= 40 ?
                'Marriage is indicated with good potential. Your relationship success grows with emotional maturity.' :
                'Marriage develops naturally when you focus on personal growth and emotional readiness.';
              
              const harmonyDesc = ump.marital_harmony?.level === 'harmonious' || ump.marital_harmony?.score >= 60 ?
                'Your chart indicates good compatibility potential. Mutual understanding and shared values form a strong foundation.' :
                ump.marital_harmony?.level === 'moderate' || ump.marital_harmony?.score >= 40 ?
                'Relationship harmony develops through conscious effort. Communication and patience strengthen the bond over time.' :
                'Relationships require intentional nurturing. Emotional awareness and direct communication are key to lasting harmony.';
              
              return (
                <>
                  {/* Marriage Overview - User-friendly */}
                  <Section title="Marriage Overview">
                    <Text>{marriageStrength}</Text>
                  </Section>
                  
                  {/* Partner Profile - Clean and natural */}
                  <Section title="Partner Profile">
                    <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Text style={{ marginBottom: '8px' }}>
                        Your ideal partner is likely to be {ump.spouse_nature?.primary_nature?.replace(/_/g, ' ')?.toLowerCase() || 'mature and grounded'}, 
                        with {ump.spouse_nature?.['7th_sign']} qualities - bringing balance to your personality.
                      </Text>
                      {ump.spouse_nature?.appearance?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {ump.spouse_nature.appearance.map((a, i) => (
                            <span key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(116,179,255,0.08)', borderRadius: '12px', color: 'rgba(255,255,255,0.7)' }}>{a}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Section>
                  
                  {/* Relationship Compatibility - User-friendly */}
                  <Section title="Relationship Compatibility">
                    <Text>{harmonyDesc}</Text>
                  </Section>
                  
                  {/* Relationship Timing - Clean user-friendly */}
                  <Section title="Relationship Timing">
                    <div style={{ padding: '14px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Text>
                        {ump.timing?.type === 'DELAYED' ? 
                          `Marriage timing tends toward the later side, with the most favorable window around ages ${ump.timing?.likely_age_range || '28-35'}. This delay often serves a purpose - allowing for greater emotional maturity and career stability before commitment.` : 
                         ump.timing?.type === 'EARLY' ? 
                          `Earlier relationship opportunities are indicated, with favorable timing around ages ${ump.timing?.likely_age_range || '24-30'}.` : 
                          `Relationship timing follows a natural progression, with the most favorable period around ages ${ump.timing?.likely_age_range || '28-35'}.`
                        }
                      </Text>
                    </div>
                  </Section>
                  
                  {/* Relationship Strengths - Clean descriptions */}
                  <Section title="Relationship Strengths">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Text>Your chart shows positive indicators for a lasting, meaningful relationship.</Text>
                      {ump.marriage_yogas?.length > 0 && (
                        <Text style={{ color: 'rgba(255,255,255,0.7)' }}>
                          Key strengths include emotional depth, loyalty, and the capacity for deep connection.
                        </Text>
                      )}
                    </div>
                  </Section>
                  
                  {/* Marriage Summary - Clean */}
                  <Section title="Marriage Summary">
                    <div style={{ padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Text>
                        {ump.confirmation_model?.count >= 4 ? 
                          "Marriage is strongly indicated in your chart. Multiple positive factors support a committed, lasting relationship." :
                          "Marriage is indicated with good potential. Your relationship success grows with emotional maturity and clear communication."
                        }
                      </Text>
                    </div>
                  </Section>
                </>
              );
            })()}
            
            {/* Marriage Timing Interpretation from Enhanced Data */}
            {enhancedData?.timing_windows?.marriage?.interpretation && (
              <Section title="When Relationships Form">
                <Text>{enhancedData?.timing_windows?.marriage?.interpretation}</Text>
              </Section>
            )}
            
            {(d5.emotional_pattern) && (
              <Section title="Emotional Nature">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>{d5.emotional_pattern?.nature || d5.emotional_pattern?.impact}</Text>
                  <Text>{d5.emotional_pattern?.development || d5.emotional_pattern?.maturation}</Text>
                  <Text>{d5.emotional_pattern?.management}</Text>
                </div>
              </Section>
            )}
            <Section title="Spouse Profile">
              {typeof d5.spouse_profile === 'object' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Tags items={d5.spouse_profile?.traits} />
                  <Text>{d5.spouse_profile?.background}</Text>
                  <Text>{d5.spouse_profile?.compatibility}</Text>
                </div>
              ) : <ProfileCards data={d5.spouse_profile} />}
            </Section>
            {d5.relationship_risks && (
              <Section title="Relationship Risks">
                <div style={{ padding: '14px', background: 'rgba(255,180,100,0.05)', borderRadius: '10px', borderLeft: '2px solid rgba(255,180,100,0.3)' }}>
                  <Text>{d5.relationship_risks?.primary || d5.relationship_risks?.primary_risk}</Text>
                  <Text style={{ marginTop: '6px' }}>{d5.relationship_risks?.resolution}</Text>
                </div>
              </Section>
            )}
          </DomainSection>

          {/* DOMAIN 6: FAMILY - With Delay Logic */}
          <DomainSection num={6} title="Family & Home" onInView={setActiveDomain} introOpen={introOpen} aiContent={aiContent} domainKey="family" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Family Role from Master Family Engine */}
            {edFamily?.role?.content && (
              <Section title="Family Role">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text>{edFamily.role.content}</Text>
                  {edFamily.role.family_tier && (
                    <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(132,204,22,0.1)', borderRadius: '12px', color: 'rgba(132,204,22,0.9)', alignSelf: 'flex-start' }}>{edFamily.role.family_tier}</span>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Parents from Master Family Engine */}
            {(edFamily?.parents?.father || edFamily?.parents?.mother) && (
              <Section title="Parents">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {edFamily.parents.father && (
                    <div style={{ padding: '12px 14px', background: 'rgba(116,179,255,0.02)', borderRadius: '8px' }}>
                      <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '4px' }}>Father</p>
                      <Text>{edFamily.parents.father}</Text>
                    </div>
                  )}
                  {edFamily.parents.mother && (
                    <div style={{ padding: '12px 14px', background: 'rgba(116,179,255,0.02)', borderRadius: '8px' }}>
                      <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '4px' }}>Mother</p>
                      <Text>{edFamily.parents.mother}</Text>
                    </div>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Children from Master Family Engine */}
            {(edFamily?.children?.indication || edFamily?.children?.happiness) && (
              <Section title="Children">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {edFamily?.children?.indication && (
                    <Text>{edFamily?.children?.indication}</Text>
                  )}
                  {edFamily?.children?.happiness && (
                    <Text style={{ color: 'rgba(255,255,255,0.7)' }}>{edFamily?.children?.happiness}</Text>
                  )}
                </div>
              </Section>
            )}
            
            {/* Fallback: Original family role */}
            {!edFamily?.role?.content && (
              <Section title="Family Role">
                {typeof d6.family_role === 'object' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Text>{d6.family_role?.position || d6.family_role?.primary}</Text>
                    <Text>{d6.family_role?.nature || d6.family_role?.why}</Text>
                  </div>
                ) : <Text>{d6.family_role}</Text>}
              </Section>
            )}
            
            {/* Fallback: Original parents */}
            {!edFamily?.parents?.father && d6.parents && (
              <Section title="Parents">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ padding: '12px 14px', background: 'rgba(116,179,255,0.02)', borderRadius: '8px' }}>
                    <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '4px' }}>Father</p>
                    <Text>{d6.parents?.father?.relationship || d6.parents?.father}</Text>
                  </div>
                  <div style={{ padding: '12px 14px', background: 'rgba(116,179,255,0.02)', borderRadius: '8px' }}>
                    <p style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(116,179,255,0.6)', marginBottom: '4px' }}>Mother</p>
                    <Text>{d6.parents?.mother?.relationship || d6.parents?.mother}</Text>
                  </div>
                </div>
              </Section>
            )}
            {d6.children && (
              <Section title="Children">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text><strong>Timing:</strong> {d6.children?.timing}</Text>
                  {d6.children?.count && <Text><strong>Count:</strong> {d6.children?.count}</Text>}
                  <Text><strong>Nature:</strong> {d6.children?.nature}</Text>
                  {(d6.children?.karmic_logic || d6.children?.note) && <Text style={{ fontStyle: 'italic', color: 'rgba(116,179,255,0.6)' }}>{d6.children?.karmic_logic || d6.children?.note}</Text>}
                </div>
              </Section>
            )}
            {d6.home_life && (
              <Section title="Home Life">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>{d6.home_life?.pattern}</Text>
                  <Text>{d6.home_life?.property}</Text>
                </div>
              </Section>
            )}
          </DomainSection>

          {/* DOMAIN 7: TRAVEL - Purpose-Driven */}
          <DomainSection num={7} title="Travel & Foreign" onInView={setActiveDomain} aiContent={aiContent} domainKey="travel" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Foreign Travel from Master Travel Engine */}
            {(edTravel?.foreign || aiContent?.life_insights?.travel) && (
              <Section title="Foreign Travel">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {edTravel?.foreign?.yogas?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {edTravel?.foreign?.yogas.map((yoga, i) => (
                        <span key={i} style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(14,165,233,0.1)', borderRadius: '12px', color: 'rgba(14,165,233,0.9)' }}>{yoga}</span>
                      ))}
                    </div>
                  )}
                  {edTravel?.foreign?.probability && (
                    <Text>Probability: {edTravel?.foreign?.probability}</Text>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Settlement from Master Travel Engine */}
            {(edTravel?.settlement || aiContent?.life_insights?.travel) && (
              <Section title="Settlement Abroad">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {edTravel?.settlement?.chances && (
                    <Text><strong>Chances:</strong> {edTravel?.settlement?.chances}</Text>
                  )}
                  {edTravel?.settlement?.favorable_directions?.length > 0 && (
                    <div style={{ marginTop: '4px' }}>
                      <div style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(14,165,233,0.6)', fontWeight: '500', marginBottom: '6px' }}>FAVORABLE DIRECTIONS</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {edTravel?.settlement?.favorable_directions.map((dir, i) => (
                          <span key={i} style={{ fontSize: 'clamp(10px, 2.8vw, 12px)', padding: '6px 12px', background: 'rgba(14,165,233,0.08)', borderRadius: '12px', color: 'rgba(14,165,233,0.9)' }}>{dir}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            )}
            
            {/* Fallback: Original travel nature */}
            {!edTravel?.foreign && (
              <Section title="Travel Nature">
                {typeof d7.travel_nature === 'object' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Text>{d7.travel_nature?.type}</Text>
                    <Text>{d7.travel_nature?.purpose}</Text>
                  </div>
                ) : <Text>{d7.travel_nature}</Text>}
              </Section>
            )}
            
            {!edTravel?.settlement && d7.foreign_connections && (
              <Section title="Foreign Connections">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>{d7.foreign_connections?.work || d7.foreign_connections?.work_related}</Text>
                  <Text>{d7.foreign_connections?.settlement}</Text>
                </div>
              </Section>
            )}
            {(d7.timing || d7.travel_timing) && (
              <Section title="Travel Timing">
                <Phases data={d7.timing || d7.travel_timing} />
              </Section>
            )}
            {!edTravel?.settlement?.favorable_directions && d7.directions && (
              <Section title="Favorable Directions">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>{d7.directions?.favorable}</Text>
                  <Text style={{ color: 'rgba(116,179,255,0.5)' }}>{d7.directions?.context}</Text>
                </div>
              </Section>
            )}
          </DomainSection>

          {/* DOMAIN 8: SPIRITUALITY - Evolution Trajectory */}
          <DomainSection num={8} title="Spirituality & Dharma" onInView={setActiveDomain} aiContent={aiContent} domainKey="spiritual" aiContentLoading={aiContentLoading} isPremium={isPremium} aiFetchDone={aiFetchDone} aiFetchFailed={aiFetchFailed} onRetryAi={fetchAiContent}>
            {/* ENHANCED: Spiritual Path from Master Spiritual Engine */}
            {(edSpiritual?.path || aiContent?.life_insights?.spiritual) && (
              <Section title="Spiritual Path">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Text>{edSpiritual?.path?.content}</Text>
                  {edSpiritual?.path?.spiritual_tier && (
                    <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', padding: '4px 10px', background: 'rgba(168,85,247,0.1)', borderRadius: '12px', color: 'rgba(168,85,247,0.9)', alignSelf: 'flex-start' }}>{edSpiritual?.path?.spiritual_tier}</span>
                  )}
                </div>
              </Section>
            )}
            
            {/* ENHANCED: Religious Nature from Master Spiritual Engine */}
            {(edSpiritual?.religion || aiContent?.life_insights?.spiritual) && (
              <Section title="Religious Nature">
                <Text>{edSpiritual?.religion?.content}</Text>
              </Section>
            )}
            
            {/* ENHANCED: Moksha Potential from Master Spiritual Engine */}
            {(edSpiritual?.moksha || aiContent?.life_insights?.spiritual) && (
              <Section title="Liberation Potential">
                <div style={{ padding: '14px', background: 'rgba(168,85,247,0.05)', borderRadius: '10px', borderLeft: '2px solid rgba(168,85,247,0.3)' }}>
                  <Text>{edSpiritual?.moksha?.content}</Text>
                  {edSpiritual?.moksha?.score > 0 && (
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: 'clamp(10px, 2.5vw, 11px)', color: 'rgba(255,255,255,0.5)' }}>Spiritual Potential:</span>
                      <span style={{ fontSize: 'clamp(11px, 3vw, 13px)', fontWeight: '500', color: 'rgba(168,85,247,0.9)' }}>
                        {edSpiritual.moksha.score >= 70 ? 'Very High' : edSpiritual.moksha.score >= 50 ? 'High' : edSpiritual.moksha.score >= 30 ? 'Moderate' : 'Developing'}
                      </span>
                    </div>
                  )}
                </div>
              </Section>
            )}
            
            {/* Fallback: Original spiritual path */}
            {!edSpiritual?.path && (
              <Section title="Spiritual Path">
                {typeof d8.spiritual_path === 'object' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Text>{d8.spiritual_path?.orientation}</Text>
                    <Text>{d8.spiritual_path?.nature}</Text>
                    <Text>{d8.spiritual_path?.approach}</Text>
                  </div>
                ) : typeof d8.spiritual_orientation === 'object' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Text>{d8.spiritual_orientation?.path}</Text>
                    <Text>{d8.spiritual_orientation?.why}</Text>
                    <Text>{d8.spiritual_orientation?.approach}</Text>
                  </div>
                ) : <Text>{d8.spiritual_orientation}</Text>}
              </Section>
            )}
            <Section title="Spiritual Evolution">
              <Phases data={d8.evolution || d8.spiritual_evolution} />
            </Section>
            {d8.practices && (
              <Section title="Recommended Practices">
                <Tags items={d8.practices?.recommended} />
                <Text style={{ marginTop: '8px' }}>{d8.practices?.purpose}</Text>
              </Section>
            )}
            {!edSpiritual?.moksha && (d8.liberation || d8.moksha_potential) && (
              <Section title="Liberation">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>{d8.liberation?.indication || d8.moksha_potential?.indication || d8.moksha_potential}</Text>
                  {(d8.liberation?.path || d8.moksha_potential?.path) && <Text>{d8.liberation?.path || d8.moksha_potential?.path}</Text>}
                </div>
              </Section>
            )}
          </DomainSection>

          {/* Powered by badge */}
          {enhancedData && (
            <div style={{ 
              textAlign: 'center', 
              padding: '16px',
              marginTop: '8px',
            }}>
              <p style={{ 
                fontSize: 'clamp(9px, 2.2vw, 10px)', 
                color: 'rgba(116,179,255,0.4)', 
                fontWeight: '400',
              }}>
                Powered by {enhancedData?.meta?.total_algorithms || enhancedData?.meta?.engines_used?.length || 17} astrological algorithms
              </p>
            </div>
          )}

          {/* Enhanced data loading indicator - silent */}
          {loadingEnhanced && !enhancedData && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(116,179,255,0.15)',
                  borderTopColor: '#74B3FF',
                  borderRadius: '50%',
                  margin: '0 auto',
                }}
              />
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '32px 20px 16px' }}>
          <p style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(10px, 2.4vw, 11px)',
            letterSpacing: '0.35em',
            color: 'rgba(243,229,171,0.55)',
            fontWeight: 300,
            textTransform: 'uppercase',
          }}>TARAANAYA</p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DomainInsights;
